import moment from "moment";
import { markRaw, reactive } from "vue";

import type { backendOld } from "~/api";
import { UNGROUPED_PSEUDO_GROUP_BY, UNSIZED_SIZE_GROUP_BY_KEY } from "~/common/groupBy";
import {
  getPropertyConfig,
  getPropertyDisplayTitle,
  getPropertyPartialTask,
  getPropertyValueFromTask,
  getPropertyValueFromTaskByDuid,
  getPropertyValueStr,
} from "~/common/properties";
import { sortTasks } from "~/common/sort";
import { checkTaskConstraints } from "~/common/taskConstraints";
import { overrideTaskAndAdjustFilters, removeFiltersThatDoNotPassForTask } from "~/components/filters/utils";
import { COMPLETED_STATUS_KINDS, UNASSIGNED_PSEUDO_ASSIGNEE_KEY } from "~/components/visualization/constants";
import { NULL_PSEUDO_VALUE, UNPRIORITIZED_PSEUDO_VALUE } from "~/constants/pseudoDuids";
import { USER_KIND_DART_AI } from "~/constants/userKinds";
import { isDefined, type TransactionResponse } from "~/shared/common";
import {
  ModelType,
  PropertyKind,
  RelationshipKindKind,
  StatusKind,
  SubtaskDisplayMode,
  TaskLinkKind,
  TaskSourceKind,
  TutorialName,
  ViewKind,
} from "~/shared/enums";
import type {
  AttachmentCreate,
  DateRange,
  Entity,
  FilterGroup,
  Option,
  PropertyAnyMultiselect,
  PropertyAnyUserWithMultipleMaybe,
  PropertyValue,
  Relationship,
  RelationshipCreate,
  RemapModalConfigItem,
  Sort,
  SummaryStatistics,
  Task,
  TaskAbsenteeMaybe,
  TaskAndUpdate,
  TaskCreate,
  TaskDocRelationshipCreate,
  TaskLink,
  TaskLinkCreate,
  TaskLinkUpdate,
  TaskUpdate,
} from "~/shared/types";
import { isString } from "~/shared/typeUtils";
import { createFilesFromAttachments } from "~/utils/api";
import {
  deepCopy,
  filterInPlace,
  getItemCountText,
  getNextTitleInSequence,
  isLexicalStateEmpty,
  makeDuid,
  makeEmptyLexicalState,
  prettyFormatList,
} from "~/utils/common";
import { makeStringComparator, stringComparator } from "~/utils/comparator";
import { formatDate } from "~/utils/date";
import { printOrCaptureMessage } from "~/utils/exceptionClient";
import { partitionReadableFiles } from "~/utils/fileUpload";
import { firework } from "~/utils/fun";
import { getOrdersBetween } from "~/utils/orderManager";
import { getUserDisplayName, isAgentUser } from "~/utils/user";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";

const TASK_PAGE_PAGINATION_KEY = "task-pages";

const getPageKey = (pageDuid: string, groupId?: string | null): string => JSON.stringify([pageDuid, groupId ?? null]);
const getPageDuidFromKey = (key: string): string => JSON.parse(key)[0];

type UpdateTaskOptions = {
  noUndo?: boolean;
  noBackend?: boolean;
  noCelebrate?: boolean;
  celebrateOverride?: boolean;
  makeDescription?: (isActive: boolean, content: string) => string;
  overrideFilters?: boolean;
  awaitBackend?: boolean;
  onRemapFailed?: () => void;
  skipRemap?: boolean;
  skipAutomations?: boolean;
};

type DateRangeChange = {
  oldStart: string | null;
  newStart: string | null;
  oldDue: string | null;
  newDue: string | null;
  remove: boolean;
};

type PropertyDiff = {
  propertyDuid: string;
  propertyKind: PropertyKind;
  diff: number | DateRangeChange;
};

export type GetTaskOptions = { includeTrashed?: boolean; includeDraft?: boolean };
export type GetTaskByDuidOptions = { includeTrashed: boolean; includeDraft: boolean; absenteeDuids?: string[] };

export type State = {
  _duidsToTasks: Map<string, Task>;
  taskDraftDuid: string | null;
  _keyToSummaryStatistics: Map<string, SummaryStatistics>;
  _pageDuidToLoadingSummaryStatistics: Map<string, boolean>;
};

export const state: State = {
  _duidsToTasks: new Map(),
  taskDraftDuid: null,
  _keyToSummaryStatistics: new Map(),
  _pageDuidToLoadingSummaryStatistics: new Map(),
};

export type Getters = {
  taskDraft: Task | undefined;
  /* Get the size of a task and its subtasks. */
  getSize: (task: Task, includeSubtasks?: boolean) => number | null;
  /* Get the start and due dates of a task and its subtasks. */
  getTaskDates: (
    task: Task,
    includeSubtasks?: boolean
  ) => { rolledUpStartAt: string | null; rolledUpDueAt: string | null };
  /* Get the start and due dates of a task for a custom date property. */
  getTaskCustomDates: (
    task: Task,
    propertyDuid: string,
    includeSubtasks?: boolean
  ) => { rolledUpStartAt: string | null; rolledUpDueAt: string | null };
  getTaskByDuid: (duid: string) => Promise<Task | undefined>;
  getTaskByDuidIfLoaded: (duid: string, options?: { assertLoaded?: boolean }) => Task | undefined;
  getTasksByDuids: (duids: string[], options: GetTaskByDuidOptions) => Promise<TaskAbsenteeMaybe[]>;
  // TODO incremental get rid of all of the ...Ordered methods and just use the non-ordered ones
  // the ordering is really bad (just by order). having these methods does more harm than good because devs think that they will actually be ordered in a useful way (e.g. by sorts) but they aren't
  // except getLoadedTasksForPageDuidOrdered, which has the actual ordering
  getTasksByDuidsOrdered: (duids: string[], options: GetTaskByDuidOptions) => Promise<TaskAbsenteeMaybe[]>;
  getTasksByDuidsIfLoaded: (duids: string[], options: GetTaskByDuidOptions) => TaskAbsenteeMaybe[];
  getTasksByDuidsOrderedIfLoaded: (duids: string[], options: GetTaskByDuidOptions) => TaskAbsenteeMaybe[];
  getAllLoadedTasksForUiOnly: (options?: GetTaskOptions) => Task[];
  getLoadedTasksForPageDuid: (pageDuid: string, groupId?: string, options?: GetTaskOptions) => Task[];
  getLoadedTasksForPageDuidOrdered: (pageDuid: string, groupId?: string, options?: GetTaskOptions) => Task[];
  getLoadedTaskCountForPageDuid: (pageDuid: string, groupId?: string) => number;
  getSummaryStatisticsForPageDuid: (pageDuid: string, groupId?: string) => SummaryStatistics;
  hasPageDuidEverBeenLoaded: (pageDuid: string, groupId?: string) => boolean;
  isLoadingSummaryStatisticsForPageDuid: (pageDuid: string) => boolean;
};

export type Actions = {
  loadTasks: (...args: Parameters<typeof backendOld.entityList.task>) => ReturnType<typeof backendOld.entityList.task>;
  loadPaginatedTaskForPageDuid: (
    pageDuid: string,
    filterGroup: FilterGroup,
    sorts: Sort[],
    validator: (task: Task) => boolean,
    options?: {
      groupId?: string;
      search?: string;
      hierarchical?: boolean;
      absentees?: boolean;
      groupByPropertyDuid?: string | null;
      onlyTrash?: boolean;
      includeTrashed?: boolean;
    }
  ) => Promise<number>;
  loadSummaryStatisticsForPageDuid: (
    pageDuid: string,
    filterGroup: FilterGroup,
    validator: (task: Task) => boolean,
    options?: {
      groupId?: string;
      search?: string;
      groupByPropertyDuid?: string | null;
      subgroupByPropertyDuid?: string | null;
      onlyTrash?: boolean;
      includeTrashed?: boolean;
    }
  ) => Promise<SummaryStatistics>;
  resetPaginationStateForPageDuid: (pageDuid: string, groupId?: string) => void;
  hardDisplayTask: (task: Task, pageDuid: string, groupId?: string) => void;
  isTaskEmpty: (task: Task) => boolean;
  isTaskAgentWorking: (task: Task) => boolean;
  createTask: (
    title: string,
    dartboardDuid: string,
    order: string,
    source: TaskSourceKind,
    partialTask?: Partial<Task>,
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<Task>;
  createTasks: (
    tasks: TaskCreate[],
    source: TaskSourceKind,
    options?: { noUndo?: boolean; relatedDocDuid?: string; awaitBackend?: boolean }
  ) => Promise<Task[]>;
  updateTasks: (updates: TaskUpdate[], options?: UpdateTaskOptions) => Promise<void>;
  updateTaskFromNlp: (
    task: Task,
    update: Partial<Task>,
    options?: { awaitBackend?: boolean; onRemapFailed?: () => void }
  ) => Promise<void>;
  replicateTasks: (
    tasksToReplicate: TaskAndUpdate[],
    options?: {
      noUndo?: boolean;
      awaitBackend?: boolean;
      propertiesAndParentOnly?: boolean;
      includeDuplicate?: boolean;
      maintainTitle?: boolean;
      maintainStatus?: boolean;
      resetSize?: boolean;
    }
  ) => Promise<Task[]>;
  deleteTasks: (tasks: Task[], options?: { awaitBackend?: boolean }) => Promise<void>;
  updateAndDeleteTasks: (updates: TaskUpdate[], deletes: Task[], options?: UpdateTaskOptions) => Promise<void>;
  emptyTrash: () => Promise<void>;
  addUsers: (
    property: PropertyAnyUserWithMultipleMaybe,
    taskDuids: string[],
    userDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  removeUsers: (
    property: PropertyAnyUserWithMultipleMaybe,
    taskDuids: string[],
    userDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  replaceUsers: (
    property: PropertyAnyUserWithMultipleMaybe,
    taskDuids: string[],
    userDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  addSubscribers: (
    taskDuids: string[],
    subscriberDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  removeSubscribers: (
    taskDuids: string[],
    subscriberDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  addOptions: (
    property: PropertyAnyMultiselect,
    taskDuids: string[],
    optionDuids: string[],
    options?: { noUndo?: boolean; createTitle?: string; awaitBackend?: boolean }
  ) => Promise<TransactionResponse[]>;
  removeOptions: (
    property: PropertyAnyMultiselect,
    taskDuids: string[],
    optionDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  replaceOptions: (
    property: PropertyAnyMultiselect,
    taskDuids: string[],
    optionDuids: string[],
    options?: { noUndo?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  addAttachments: (taskDuid: string, files: File[], options?: { awaitBackend?: boolean }) => Promise<void>;
  removeAttachment: (taskDuid: string, attachmentDuid: string, options?: { awaitBackend?: boolean }) => Promise<void>;
  addLink: (
    taskDuid: string,
    kind: TaskLinkKind,
    url: string,
    title: string | null,
    options?: { awaitBackend?: boolean }
  ) => Promise<void>;
  updateLink: (taskDuid: string, linkUpdate: TaskLinkUpdate, options?: { awaitBackend?: boolean }) => Promise<void>;
  deleteLink: (taskDuid: string, linkDuid: string, options?: { awaitBackend?: boolean }) => Promise<void>;
  addUpdateAndDeleteLinks: (
    taskDuid: string,
    creates: TaskLinkCreate[],
    updates: TaskLinkUpdate[],
    deletes: string[],
    options?: { awaitBackend?: boolean }
  ) => Promise<void>;
  createRelationship: (
    sourceDuid: string,
    targetDuid: string,
    relationshipKindDuid: string,
    options?: { noBackend?: boolean; awaitBackend?: boolean }
  ) => Promise<Relationship | undefined>;
  deleteRelationship: (
    sourceDuid: string,
    relationshipDuid: string,
    options?: { noBackend?: boolean; awaitBackend?: boolean }
  ) => Promise<void>;
  deleteAndCreateRelationshipAndUpdateTask: (
    relationshipDelete?: { taskDuid: string; relationshipDuid: string },
    relationshipCreate?: { sourceDuid: string; targetDuid: string; relationshipKindDuid: string },
    taskUpdate?: TaskUpdate,
    options?: { noBackend?: boolean; celebrateOverride?: boolean; awaitBackend?: boolean; onRemapFailed?: () => void }
  ) => Promise<void>;
  $replicateTaskRecursive: (
    task: Task,
    order: string,
    partialTask?: Partial<Task>,
    options?: {
      propertiesAndParentOnly?: boolean;
      includeDuplicate?: boolean;
      maintainTitle?: boolean;
      maintainStatus?: boolean;
      resetSize?: boolean;
    }
  ) => Promise<{
    replicatedTask: Task;
    taskCreates: TaskCreate[];
    relationshipCreates: RelationshipCreate[];
    taskDocRelationshipCreates: TaskDocRelationshipCreate[];
    linkCreates: TaskLinkCreate[];
    attachmentCreates: AttachmentCreate[];
  }>;
  $createRelationshipOneDirectionNoBackend: (
    relationshipDuid: string,
    modTaskDuid: string,
    otherTaskDuid: string,
    relationshipKindDuid: string,
    isForward?: boolean
  ) => Relationship | undefined;
  $updateTaskSubscribersNoBackend: (task: Task, potentialSubscriberDuids: string[]) => string[];
  $addLinksNoBackend: (task: Task, linkCreates: TaskLinkCreate[]) => void;
  $updateLinksNoBackend: (task: Task, linkUpdates: TaskLinkUpdate[]) => void;
  $deleteLinksNoBackend: (task: Task, linkDuids: string[]) => void;
  $isPrimaryDraft: (task: Task) => boolean;
  $setDraftTask: (task: Task) => void;
  $updateDartboardTopTaskOrderNoBackend: (
    dartboardDuid: string,
    changingTask: Task,
    oldTaskOrder: string | undefined
  ) => void;
  $createOrUpdateTask: (
    task: Task,
    extras?: { hardUpdate?: boolean; skipSummaryStatisticsAndRollup?: boolean }
  ) => void;
  $deleteTask: (task: Task) => void;
  /* Remap incompatible properties of tasks using a modal, returns the updated task updates or an empty array if the user cancels */
  $remapPropertiesOfTasksAsNeeded: (updates: TaskUpdate[], onRemapFailed: () => void) => Promise<TaskUpdate[]>;
  $updateSummaryStatisticsForTaskChange: (oldTask: Task | undefined, newTask: Task | undefined) => void;
  $getDateRollupFromChildren: (
    ancestor: Task,
    propertyDuid: string,
    propertyKind: PropertyKind,
    excludingChildDuid?: string | null
  ) => [string | null, string | null];
  $addOrRemoveRollupUiOnly: (task: Task, options: { remove: boolean }) => void;
  $updateRollupForTaskUpdatesUiOnly: (task: Task, oldTask?: Task) => void;
  $updateRollupUiOnly: (task: Task, propertyDiffs: PropertyDiff[]) => void;
  $cleanupTasksPropertyValuesNoBackend: (
    entityDuids: string[],
    propertyDuids: string[],
    options?: { removeAllValues?: boolean }
  ) => void;
};

const SINGULAR_TASK_FIELD_NAME_REMAP = new Map([
  ["statusDuid", "status"],
  ["assigneeDuids", "assignee"],
  ["reviewerDuids", "reviewer"],
  ["subscriberDuids", "subscriber"],
  ["tagDuids", "tag"],
  ["startAt", "start date"],
  ["dueAt", "due date"],
  ["remindAt", "reminder"],
]);
const PLURAL_TASK_FIELD_NAME_REMAP = new Map([
  ["statusDuid", "statuses"],
  ["priority", "priorities"],
]);

const makeDefaultMakeDescription =
  (field: string | undefined, count: number) => (isActive: boolean, content: string) => {
    if (field === undefined) {
      return `${isActive ? "updating" : "updated"} ${content}`;
    }
    const singularField = SINGULAR_TASK_FIELD_NAME_REMAP.get(field) ?? field;
    const pluralField = PLURAL_TASK_FIELD_NAME_REMAP.get(field) ?? `${field}s`;
    return `set${isActive ? "ting" : ""} ${count !== 1 ? pluralField : singularField} for ${content}`;
  };

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  taskDraft() {
    if (!this.taskDraftDuid) {
      return undefined;
    }

    return this.getTaskByDuidIfLoaded(this.taskDraftDuid);
  },
  getTasksByDuidsIfLoaded() {
    return (duids, options) => {
      const { includeTrashed, includeDraft, absenteeDuids = [] } = options;
      const getAll = (duidsInner: string[]) =>
        [...new Set(duidsInner)]
          .map((e) => this.getTaskByDuidIfLoaded(e))
          .filter(isDefined)
          .filter((e) => (includeTrashed || !e.inTrash) && (includeDraft || !e.drafterDuid));
      const absentees = getAll(absenteeDuids).map((e) => ({ ...e, absentee: true }));
      return [...getAll(duids), ...absentees];
    };
  },
  getTasksByDuidsOrderedIfLoaded() {
    return (duids, options) => this.getTasksByDuidsIfLoaded(duids, options).sort(makeStringComparator((e) => e.order));
  },
  getSize() {
    return (task, includeSubtasks = false) => {
      const taskSize = task.size ?? null;
      if (!includeSubtasks) {
        return taskSize;
      }

      const subtasksSize = task.rollup[this.defaultSizeProperty.duid] ?? 0;
      if (typeof subtasksSize !== "number") {
        return taskSize;
      }

      return taskSize !== null ? taskSize + subtasksSize : subtasksSize > 0 ? subtasksSize : null;
    };
  },
  getTaskDates() {
    return (task: Task, includeSubtasks) => {
      const isRollupEnabled = includeSubtasks ?? this.defaultDatesProperty.adtl.rollup;
      if (!isRollupEnabled) {
        return { rolledUpStartAt: task.startAt, rolledUpDueAt: task.dueAt };
      }

      const datesRollupValue = task.rollup[this.defaultDatesProperty.duid];
      const rollupStart = Array.isArray(datesRollupValue) && datesRollupValue.length > 0 ? datesRollupValue[0] : null;
      const rollupDue = Array.isArray(datesRollupValue) && datesRollupValue.length > 1 ? datesRollupValue[1] : null;

      return {
        rolledUpStartAt: task.startAt ?? rollupStart,
        rolledUpDueAt: task.dueAt ?? rollupDue,
      };
    };
  },
  getTaskCustomDates() {
    return (task: Task, propertyDuid: string, includeSubtasks) => {
      const property = this.getPropertyByDuid(propertyDuid);
      if (!property) {
        return { rolledUpStartAt: null, rolledUpDueAt: null };
      }
      const isRollupEnabled = includeSubtasks ?? (property.kind === PropertyKind.DATES && property.adtl.rollup);

      const taskDates = task.properties[propertyDuid] as DateRange | undefined;
      const taskStart = taskDates && taskDates.length > 0 ? taskDates[0] : null;
      const taskDue = taskDates && taskDates.length > 1 ? taskDates[1] : null;
      if (!isRollupEnabled) {
        return { rolledUpStartAt: taskStart, rolledUpDueAt: taskDue };
      }

      const rollupDates = task.rollup[propertyDuid] as DateRange | undefined;
      const rollupStart = rollupDates && rollupDates.length > 0 ? rollupDates[0] : null;
      const rollupDue = rollupDates && rollupDates.length > 1 ? rollupDates[1] : null;

      return {
        rolledUpStartAt: taskStart ?? rollupStart,
        rolledUpDueAt: taskDue ?? rollupDue,
      };
    };
  },
  getTaskByDuid() {
    return async (duid) => {
      const task = this.getTaskByDuidIfLoaded(duid);
      if (task) {
        return task;
      }

      if (this.$isEntityDuidBlacklisted(duid)) {
        return undefined;
      }

      let taskFetched = await this.$batchers.entityListManager.getEntityByDuid("tasks", duid);
      if (taskFetched) {
        this.$createOrUpdateTask(taskFetched, { skipSummaryStatisticsAndRollup: true });
        return taskFetched;
      }

      if (this.$useAppStore().currentPage?.kind === ViewKind.TRASH) {
        const { data } = await this.loadTasks({ duids: [duid], inTrash: true }, 0, { limit: 1 });
        taskFetched = data.results[0];
      }

      if (!taskFetched) {
        this.$blacklistEntityDuid("tasks", duid);
        return undefined;
      }

      return taskFetched;
    };
  },
  getTaskByDuidIfLoaded() {
    return (duid, options = {}) => {
      const { assertLoaded = false } = options;
      const task = this._duidsToTasks.get(duid);
      if (assertLoaded && !task) {
        printOrCaptureMessage("Assert loaded task with duid not found", { duid });
      }
      return task;
    };
  },
  getTasksByDuids() {
    return async (duids, options) => {
      const { includeTrashed, includeDraft, absenteeDuids = [] } = options;
      const getAll = async (duidsInner: string[]) => {
        const tasks = await Promise.all([...new Set(duidsInner)].map((e) => this.getTaskByDuid(e)));
        return tasks
          .filter(isDefined)
          .filter((e) => (includeTrashed || !e.inTrash) && (includeDraft || !e.drafterDuid));
      };

      const [mainTasks, absentees] = await Promise.all([getAll(duids), getAll(absenteeDuids)]);
      return [...mainTasks, ...absentees.map((e) => ({ ...e, absentee: true }))];
    };
  },
  getTasksByDuidsOrdered() {
    return async (duids, options) => {
      const tasks = await this.getTasksByDuids(duids, options);
      return tasks.sort(makeStringComparator((e) => e.order));
    };
  },
  getAllLoadedTasksForUiOnly() {
    return (options = {}) =>
      [...this._duidsToTasks.values()].filter(
        (e) => (options.includeTrashed || !e.inTrash) && (options.includeDraft || !e.drafterDuid)
      );
  },
  getLoadedTasksForPageDuid() {
    return (pageDuid, groupId, options = {}) => {
      const key = getPageKey(pageDuid, groupId);
      const paginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, key);

      if (!paginationState) {
        return [];
      }

      // Return a temporary list of tasks while initializing for better UX
      if (!paginationState.loadedAll && paginationState.duids.size === 0) {
        return [...this.getAllLoadedTasksForUiOnly(options)].filter((e) => paginationState.validator?.(e));
      }

      return [...paginationState.duids.values()]
        .map((e) => this.getTaskByDuidIfLoaded(e))
        .filter(isDefined)
        .filter((e) => paginationState.validator?.(e))
        .filter((task) => (options.includeTrashed || !task.inTrash) && (options.includeDraft || !task.drafterDuid));
    };
  },
  getLoadedTasksForPageDuidOrdered() {
    return (pageDuid, groupId, options = {}) => {
      const appStore = this.$useAppStore();
      const shouldApplyGroupSort =
        groupId === undefined && !!appStore.groupBy && appStore.groupBy !== UNGROUPED_PSEUDO_GROUP_BY;
      return sortTasks(
        this,
        this.getLoadedTasksForPageDuid(pageDuid, groupId, options),
        appStore.sorts,
        shouldApplyGroupSort ? appStore.groupByDefinition : null,
        appStore.subtaskDisplayMode === SubtaskDisplayMode.FLAT || shouldApplyGroupSort
      );
    };
  },
  getLoadedTaskCountForPageDuid() {
    return (pageDuid, groupId) => {
      const key = getPageKey(pageDuid, groupId);
      const paginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, key);

      if (!paginationState) {
        return 0;
      }

      return paginationState.duids.size;
    };
  },
  getSummaryStatisticsForPageDuid() {
    return (pageDuid, groupId) => {
      const key = getPageKey(pageDuid, groupId);
      return this._keyToSummaryStatistics.get(key) ?? { count: 0, points: 0, completedCount: 0, completedPoints: 0 };
    };
  },
  isLoadingSummaryStatisticsForPageDuid() {
    return (pageDuid) => this._pageDuidToLoadingSummaryStatistics.get(pageDuid) ?? false;
  },
  hasPageDuidEverBeenLoaded() {
    return (pageDuid, groupId) => {
      const key = getPageKey(pageDuid, groupId);
      const paginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, key);
      return !!paginationState;
    };
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async loadTasks(...args) {
    const response = await this.$backendOld.entityList.task(...args);
    response.data.results.forEach((task) => {
      if (this.getTaskByDuidIfLoaded(task.duid)) {
        return;
      }
      this.$createOrUpdateTask(task, { skipSummaryStatisticsAndRollup: true });
    });
    return response;
  },
  async loadPaginatedTaskForPageDuid(pageDuid, filterGroup, sorts, validator, options = {}) {
    const {
      groupId = null,
      search = "",
      hierarchical = false,
      absentees = false,
      groupByPropertyDuid = null,
      onlyTrash = false,
      includeTrashed = false,
    } = options;
    const pageStore = this.$usePageStore();
    const key = getPageKey(pageDuid, groupId);

    let paginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, key);

    // TODO incremental reevaluate this
    // Update the validator when reusing an existing pagination state
    // This is crucial when filters change and we reset the pagination
    if (paginationState) {
      paginationState.validator = validator as (e: Entity) => boolean;
    }

    const { data } = await (pageStore.isPublicView
      ? this.$backendOld.entityList.getPublicViewTasks(pageDuid, filterGroup, paginationState?.duids.size ?? 0, {
          hierarchical,
          absentees,
          groupByPropertyDuid,
        })
      : this.$backendOld.entityList.queryTasks(filterGroup, sorts, search, paginationState?.duids.size ?? 0, {
          hierarchical,
          absentees,
          groupByPropertyDuid,
          onlyTrash,
          includeTrashed,
        }));
    data.results.forEach((task) => {
      if (this.getTaskByDuidIfLoaded(task.duid)) {
        return;
      }
      this.$createOrUpdateTask(task, { skipSummaryStatisticsAndRollup: true });
    });

    if (!this._keyToSummaryStatistics.has(key)) {
      this._keyToSummaryStatistics.set(key, data.summaryStatistics);
    }

    if (!paginationState) {
      paginationState = this.$createPaginationState(
        TASK_PAGE_PAGINATION_KEY,
        key,
        // TODO incremental, make a better sort comparator, accept all for now. I guess this is the same as the old "TODO why are task and notification comparators inverted?" https://github.com/its-dart/dart/pull/2805/files/a6d4657495ad6e674cf05f59060d2733c8586ef6#r2249515223
        () => 0,
        validator as (e: Entity) => boolean
      );
    }
    this.$addEntitiesToPaginationState(TASK_PAGE_PAGINATION_KEY, key, data.results, data.count);

    const missingLocalTasks = this.getAllLoadedTasksForUiOnly({
      includeDraft: false,
      includeTrashed,
    }).filter((e) => !paginationState.duids.has(e.duid) && paginationState.validator?.(e));
    if (missingLocalTasks.length > 0) {
      this.$addEntitiesToPaginationState(TASK_PAGE_PAGINATION_KEY, key, missingLocalTasks, null);
    }

    return data.count;
  },
  async loadSummaryStatisticsForPageDuid(pageDuid, filterGroup, validator, options) {
    const {
      groupId = null,
      groupByPropertyDuid = null,
      subgroupByPropertyDuid = null,
      search = "",
      onlyTrash = false,
      includeTrashed = false,
    } = options ?? {};
    const appStore = this.$useAppStore();
    const pageStore = this.$usePageStore();

    // Create a pagination state if it doesn't exist
    const key = getPageKey(pageDuid, groupId);
    if (!this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, key)) {
      this.$createPaginationState(
        TASK_PAGE_PAGINATION_KEY,
        key,
        // TODO incremental, make a better sort comparator, accept all for now. I guess this is the same as the old "TODO why are task and notification comparators inverted?" https://github.com/its-dart/dart/pull/2805/files/a6d4657495ad6e674cf05f59060d2733c8586ef6#r2249515223
        () => 0,
        validator as (e: Entity) => boolean
      );
    }

    this._pageDuidToLoadingSummaryStatistics.set(pageDuid, true);
    const { data } = await (pageStore.isPublicView
      ? this.$backendOld.entityList.getPublicViewSummaryStatistics(pageDuid, filterGroup, {
          groupByPropertyDuid,
          subgroupByPropertyDuid,
        })
      : this.$backendOld.entityList.getSummaryStatistics(filterGroup, {
          search,
          groupByPropertyDuid,
          subgroupByPropertyDuid,
          onlyTrash,
          includeTrashed,
        }));

    this._keyToSummaryStatistics.set(getPageKey(pageDuid), data.overallStatistics);
    if (data.groupedStatistics) {
      Object.entries(data.groupedStatistics).forEach(([groupIdInner, stats]) => {
        const groupKey = getPageKey(pageDuid, groupIdInner);
        this._keyToSummaryStatistics.set(groupKey, stats);
      });
    }
    if (data.subgroupedStatistics) {
      Object.entries(data.subgroupedStatistics).forEach(([subgroupIdInner, stats]) => {
        const subgroupKey = getPageKey(pageDuid, subgroupIdInner);
        this._keyToSummaryStatistics.set(subgroupKey, stats);
      });
    }
    if (data.cellStatistics) {
      Object.entries(data.cellStatistics).forEach(([columnId, swimlanes]) => {
        Object.entries(swimlanes).forEach(([swimlaneId, stats]) => {
          const cellKey = getPageKey(pageDuid, `${columnId}-${swimlaneId}`);
          this._keyToSummaryStatistics.set(cellKey, stats);
        });
      });
    }

    const groupByDef = groupByPropertyDuid
      ? appStore.groupByDefinitionList.find((e) => e.property.duid === groupByPropertyDuid)
      : null;
    const subgroupByDef = subgroupByPropertyDuid
      ? appStore.groupByDefinitionList.find((e) => e.property.duid === subgroupByPropertyDuid)
      : null;
    const setZeroIfMissing = (subKey: string) => {
      const k = getPageKey(pageDuid, subKey);
      if (!this._keyToSummaryStatistics.has(k)) {
        this._keyToSummaryStatistics.set(k, { count: 0, points: 0, completedCount: 0, completedPoints: 0 });
      }
    };
    groupByDef?.groups.forEach((g) => setZeroIfMissing(g.id));
    subgroupByDef?.groups.forEach((g) => setZeroIfMissing(g.id));
    if (groupByDef && subgroupByDef) {
      groupByDef.groups.forEach((col) => {
        subgroupByDef.groups.forEach((sw) => setZeroIfMissing(`${col.id}-${sw.id}`));
      });
    }

    this._pageDuidToLoadingSummaryStatistics.set(pageDuid, false);

    return data.overallStatistics;
  },
  resetPaginationStateForPageDuid(pageDuid, groupId) {
    const key = getPageKey(pageDuid, groupId);
    this.$clearPaginationState(TASK_PAGE_PAGINATION_KEY, key);
    // Clear all summary statistics for this page
    [...this._keyToSummaryStatistics.keys()]
      .filter((e) => getPageDuidFromKey(e) === pageDuid)
      .forEach((e) => this._keyToSummaryStatistics.delete(e));
  },
  hardDisplayTask(task, pageDuid, groupId) {
    const key = getPageKey(pageDuid, groupId);
    this.$addEntitiesToPaginationState(TASK_PAGE_PAGINATION_KEY, key, [task], null);
  },
  isTaskEmpty(task) {
    return (
      task.title.trim() === "" &&
      isLexicalStateEmpty(task.description) &&
      task.tagDuids.length === 0 &&
      task.links.length === 0 &&
      task.attachmentDuids.length === 0 &&
      this.getRelationshipsByKindKind(task, RelationshipKindKind.PARENT_OF, true).length === 0 &&
      task.priority === null &&
      task.size === null &&
      task.startAt === null &&
      task.dueAt === null
    );
  },
  isTaskAgentWorking(task) {
    return this.getUsersByDuidsOrdered(task.assigneeDuids).some(
      (user) => isAgentUser(user) && user.agent?.activeSessions?.some((e) => e.taskDuid === task.duid)
    );
  },
  async createTask(title, dartboardDuid, order, source, partialTask, options) {
    const tasks = await this.createTasks(
      [
        {
          duid: makeDuid(),
          title,
          dartboardDuid,
          order,
          ...partialTask,
        },
      ],
      source,
      options
    );
    return tasks[0];
  },
  async createTasks(tasks, source, options = {}) {
    const { noUndo = false, relatedDocDuid, awaitBackend = false } = options;

    const userStore = this.$useUserStore();

    const taskKindDuid = this.defaultTaskKind.duid;
    const statusDuid = this.defaultDefaultUnstartedStatus.duid;
    const userDuid = userStore.duid;
    const now = new Date().toISOString();
    const newTasks = tasks.map((taskCreate) => {
      const task: Task = {
        sourceKind: source,
        createdAt: now,
        createdByDuid: userDuid,
        updatedAt: now,
        updatedByDuid: userDuid,
        drafterDuid: null,
        inTrash: false,
        recommendationDuid: null,
        dartboardDuid: "",
        order: "",
        expanded: true,
        kindDuid: taskKindDuid,
        title: "",
        description: makeEmptyLexicalState(),
        statusDuid,
        assigneeDuids: [],
        reviewerDuids: [],
        subscriberDuids: [userDuid],
        tagDuids: [],
        relationships: [],
        docRelationships: [],
        links: [],
        attachmentDuids: [],
        priority: null,
        size: null,
        startAt: null,
        dueAt: null,
        timeTracking: [],
        remindAt: null,
        completedAt: null,
        recurrence: null,
        recursNextAt: null,
        properties: {},
        rollup: {},
        ...taskCreate,
      };

      const overriddenTask = overrideTaskAndAdjustFilters(this, task, taskCreate, source);
      Object.assign(overriddenTask, this.$getAssignCreatorOnCreateFields(overriddenTask, taskCreate));
      if (overriddenTask.assigneeDuids) {
        overriddenTask.subscriberDuids = [
          ...new Set([...overriddenTask.subscriberDuids, ...overriddenTask.assigneeDuids]),
        ];
      }

      const suggestedUpdate = checkTaskConstraints(overriddenTask, this);
      if (Object.keys(suggestedUpdate).length > 0) {
        Object.assign(overriddenTask, suggestedUpdate);
      }

      const status = this.getStatusByDuid(overriddenTask.statusDuid);
      if (status && COMPLETED_STATUS_KINDS.has(status.kind) && overriddenTask.completedAt === null) {
        overriddenTask.completedAt = now;
      }

      this.$createOrUpdateTask(overriddenTask);
      userStore.addRecentTask(overriddenTask);
      this._tasksDuidsWithoutComments.add(overriddenTask.duid);
      return overriddenTask;
    });

    const newTaskRelationships = newTasks.flatMap((newTask) =>
      newTask.relationships
        .map(({ duid, kindDuid, targetDuid, isForward }) => [
          this.$createRelationshipOneDirectionNoBackend(
            duid,
            targetDuid,
            newTask.duid,
            kindDuid,
            isForward !== undefined ? !isForward : undefined
          ),
          targetDuid,
        ])
        .filter((e): e is [Relationship, string] => !!e[0])
        .map(([relationship, sourceDuid]) => ({ ...relationship, sourceDuid }))
    );

    const newDocRelationships = relatedDocDuid
      ? newTasks.map((newTask) => {
          const taskDocRelationshipCreate = { duid: makeDuid(), taskDuid: newTask.duid, docDuid: relatedDocDuid };
          this.$createTaskDocRelationship(taskDocRelationshipCreate);
          return taskDocRelationshipCreate;
        })
      : [];

    const newTasksWithoutReadOnlyFields = newTasks.map((e) => ({ ...e, rollup: undefined }));
    const backendAction = this.$backend.task.createMany(
      newTasksWithoutReadOnlyFields,
      newTaskRelationships,
      newDocRelationships
    );
    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const content = newTasks.length > 1 ? getItemCountText(newTasks.length, "task") : newTasks[0].title;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: newTasks.map((e) => e.duid) },
        [`created ${content} tasks`, `creating ${content}`],
        () => this.deleteTasks(newTasks),
        () => this.createTasks(tasks, source, { noUndo: true })
      );
    }

    if (newTasks.some((e) => e.drafterDuid === null)) {
      userStore.updateTutorialStatuses([{ name: TutorialName.MAKE_A_TASK, status: 2 }], false, true);
    }

    return newTasks;
  },
  async updateTasks(updates, options) {
    await this.updateAndDeleteTasks(updates, [], options);
  },
  async updateTaskFromNlp(task, update, options) {
    const { duid, assigneeDuids, tagDuids, subscriberDuids } = task;
    const updateNorm: TaskUpdate = {
      ...update,
      tagDuids: [...new Set([...tagDuids, ...(update.tagDuids ?? [])])],
      duid,
      title: update.title ?? "",
    };
    if (update.properties !== undefined) {
      updateNorm.properties = { ...task.properties, ...update.properties };
    }
    if (update.assigneeDuids && update.assigneeDuids.length > 0) {
      updateNorm.subscriberDuids = [...new Set([...subscriberDuids, ...update.assigneeDuids])];
      if (this.$useTenantStore().multipleAssigneesEnabled) {
        updateNorm.assigneeDuids = [...new Set([...assigneeDuids, ...update.assigneeDuids])];
      }
    }
    await this.updateTasks([updateNorm], { ...options, noUndo: true });
  },
  async replicateTasks(tasksToReplicate, options = {}) {
    const {
      noUndo = false,
      propertiesAndParentOnly = false,
      includeDuplicate = false,
      awaitBackend = false,
      maintainTitle = false,
      maintainStatus = false,
      resetSize = false,
    } = options;

    if (tasksToReplicate.length === 0) {
      return [];
    }

    const parentKindDuid = this.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;

    let filteredTasksToReplicate = tasksToReplicate;
    if (!propertiesAndParentOnly) {
      const taskDuids = new Set(tasksToReplicate.map((e) => e.task.duid));
      filteredTasksToReplicate = filteredTasksToReplicate.filter(({ task }) =>
        this.getAncestorDuidsUiOnly(task).every((ancestor) => !taskDuids.has(ancestor))
      );
    }

    const tasksToCreate: TaskCreate[] = [];
    const relationshipsToCreate: RelationshipCreate[] = [];
    const taskDocRelationshipsToCreate: TaskDocRelationshipCreate[] = [];
    const linksToCreate: TaskLinkCreate[] = [];
    const attachmentsToCreate: AttachmentCreate[] = [];

    const replicatedTasks: Task[] = [];
    await Promise.all(
      filteredTasksToReplicate.map(async ({ task, order, partialTask }) => {
        const normPartialTask = partialTask ?? {};
        normPartialTask.relationships = normPartialTask.relationships ?? [];

        const parentRelationship = this.getRelationshipsByKindKind(task, RelationshipKindKind.PARENT_OF, false)[0];
        if (parentRelationship && !normPartialTask.relationships.some((e) => e.kindDuid === parentKindDuid)) {
          // TODO unclear why reactive is needed here. should figure it out and remove
          const replicateParentRelationship = reactive({ ...parentRelationship, duid: makeDuid() });
          normPartialTask.relationships.push(replicateParentRelationship);
        }
        const {
          replicatedTask,
          taskCreates,
          relationshipCreates,
          taskDocRelationshipCreates,
          linkCreates,
          attachmentCreates,
        } = await this.$replicateTaskRecursive(task, order, normPartialTask, {
          propertiesAndParentOnly,
          includeDuplicate,
          maintainTitle,
          maintainStatus,
          resetSize,
        });

        replicatedTasks.push(replicatedTask);
        tasksToCreate.push(...taskCreates);
        relationshipsToCreate.push(...relationshipCreates);
        taskDocRelationshipsToCreate.push(...taskDocRelationshipCreates);
        linksToCreate.push(...linkCreates);
        attachmentsToCreate.push(...attachmentCreates);

        if (this.$isPrimaryDraft(replicatedTask)) {
          this.taskDraftDuid = replicatedTask.duid;
        }
      })
    );

    const taskCreateAction = this.$backend.task.createMany(
      tasksToCreate,
      relationshipsToCreate,
      taskDocRelationshipsToCreate,
      linksToCreate,
      attachmentsToCreate
    );

    if (awaitBackend) {
      await taskCreateAction;
    }

    const content =
      replicatedTasks.length > 1
        ? getItemCountText(replicatedTasks.length, "task")
        : `task ${replicatedTasks[0].title}`;

    if (!noUndo) {
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: replicatedTasks.map((e) => e.duid) },
        [`Replicated ${content}`, `replicating ${content}`],
        async () =>
          this.deleteTasks(
            await this.getTasksByDuidsOrdered(
              tasksToCreate.map((e) => e.duid),
              { includeTrashed: false, includeDraft: false }
            )
          ),
        () => this.replicateTasks(tasksToReplicate, options)
      );
    }

    return replicatedTasks;
  },
  async deleteTasks(tasks, options) {
    this.updateAndDeleteTasks([], tasks, { ...options, noUndo: true });
  },
  async emptyTrash() {
    this.getAllLoadedTasksForUiOnly({ includeTrashed: true, includeDraft: true })
      .filter((e) => e.inTrash)
      .forEach((e) => {
        this.$deleteTask(e);
      });
    await this.$backendOld.tasks.emptyTrash();
  },
  async updateAndDeleteTasks(updates, deletes, options = {}) {
    const {
      noUndo = false,
      noBackend = false,
      noCelebrate = false,
      celebrateOverride = false,
      makeDescription,
      overrideFilters = false,
      awaitBackend = false,
      onRemapFailed = () => undefined,
      skipRemap = false,
      skipAutomations = false,
    } = options;

    // Remap can only be skipped if noBackend is true
    if (!(skipRemap && noBackend)) {
      // eslint-disable-next-line no-param-reassign
      updates = await this.$remapPropertiesOfTasksAsNeeded(updates, onRemapFailed);
    }

    if (updates.length === 0 && deletes.length === 0) {
      return;
    }

    const appStore = this.$useAppStore();
    const tenantStore = this.$useTenantStore();
    const userStore = this.$useUserStore();
    const updaterDuid = userStore.duid;

    const automatedUpdates: TaskUpdate[] = [];
    if (!skipAutomations && tenantStore.closeParentOnCloseAllSubtasks) {
      const completedStatusDuids = this.defaultCompletedStatusDuids;
      const newlyCompletedDuidsList = updates
        .filter((e) => e.statusDuid && completedStatusDuids.has(e.statusDuid))
        .map((e) => e.duid);

      const finishedStatusDuid = this.defaultDefaultFinishedStatus.duid;
      const newlyCompletedDuids = new Set(newlyCompletedDuidsList);
      const queue = this.getTasksByDuidsIfLoaded([...newlyCompletedDuids], {
        includeTrashed: false,
        includeDraft: false,
      });
      while (queue.length !== 0) {
        const curr = queue.shift() as Task;
        const parent = this.getParentTaskIfLoaded(curr);
        if (!parent || completedStatusDuids.has(parent.statusDuid) || newlyCompletedDuids.has(parent.duid)) {
          continue;
        }
        const siblings = this.getTasksByDuidsIfLoaded(
          this.getRelationshipsByKindKind(parent, RelationshipKindKind.PARENT_OF, true).map((e) => e.targetDuid),
          { includeTrashed: false, includeDraft: false }
        );
        if (
          siblings.length === 0 ||
          siblings.some((e) => !completedStatusDuids.has(e.statusDuid) && !newlyCompletedDuids.has(e.duid))
        ) {
          continue;
        }
        newlyCompletedDuids.add(parent.duid);
        queue.push(parent);
        automatedUpdates.push({ duid: parent.duid, statusDuid: finishedStatusDuid });
      }
    }
    if (!skipAutomations && tenantStore.startParentOnStartAnySubtask) {
      const unstartedStatusDuids = new Set(
        this.getStatusesByKinds([StatusKind.UNSTARTED], this.defaultStatusProperty).map((e) => e.duid)
      );
      const startedStatusDuids = new Set(
        this.getStatusesByKinds([StatusKind.STARTED], this.defaultStatusProperty).map((e) => e.duid)
      );
      const newlyStartedDuidsList = updates
        .filter((e) => e.statusDuid && startedStatusDuids.has(e.statusDuid))
        .map((e) => e.duid);
      const newlyStartedDuids = new Set(newlyStartedDuidsList);
      const queue = this.getTasksByDuidsIfLoaded([...newlyStartedDuids], {
        includeTrashed: false,
        includeDraft: false,
      });
      while (queue.length !== 0) {
        const curr = queue.shift() as Task;
        const parent = this.getParentTaskIfLoaded(curr);
        if (!parent || !unstartedStatusDuids.has(parent.statusDuid) || newlyStartedDuids.has(parent.duid)) {
          continue;
        }

        const currUpdate = [...updates, ...automatedUpdates].find((update) => update.duid === curr.duid);
        const currStatusDuid = currUpdate?.statusDuid ?? curr.statusDuid;

        automatedUpdates.push({ duid: parent.duid, statusDuid: currStatusDuid });
        newlyStartedDuids.add(parent.duid);
        queue.push(parent);
      }
    }
    if (!skipAutomations && tenantStore.moveSubtasksOnMoveParent) {
      updates.forEach((update) => {
        if (!update.dartboardDuid) {
          return;
        }
        const oldTask = this.getTaskByDuidIfLoaded(update.duid);
        if (!oldTask) {
          return;
        }
        const oldDartboardDuid = oldTask.dartboardDuid;
        this.getTasksByDuidsIfLoaded(
          this.getSourceOrTargetDuidsUiOnly(oldTask, RelationshipKindKind.PARENT_OF, false, true),
          { includeTrashed: false, includeDraft: false }
        ).forEach((task) => {
          if (task.dartboardDuid !== oldDartboardDuid) {
            return;
          }
          automatedUpdates.push({ duid: task.duid, dartboardDuid: update.dartboardDuid });
        });
      });
    }
    if (!skipAutomations && tenantStore.updateSubtasksStatusOnUpdateParentStatus) {
      updates.forEach((update) => {
        if (!update.statusDuid) {
          return;
        }
        const oldTask = this.getTaskByDuidIfLoaded(update.duid);
        if (!oldTask) {
          return;
        }
        const newStatus = this.getStatusByDuid(update.statusDuid);
        if (!newStatus || !COMPLETED_STATUS_KINDS.has(newStatus.kind)) {
          return;
        }

        this.getTasksByDuidsIfLoaded(
          this.getSourceOrTargetDuidsUiOnly(oldTask, RelationshipKindKind.PARENT_OF, false, true),
          { includeTrashed: false, includeDraft: false }
        ).forEach((task) => {
          const taskStatus = this.getStatusByDuid(task.statusDuid);
          if (!taskStatus || COMPLETED_STATUS_KINDS.has(taskStatus.kind)) {
            return;
          }

          automatedUpdates.push({ duid: task.duid, statusDuid: update.statusDuid });
        });
      });
    }
    if (!skipAutomations && tenantStore.updateBlockeeDatesOnUpdateBlockerDueDate) {
      const newlyUpdatedDuidToDueAt = new Map(
        updates
          .filter((e) => e.dueAt && e.dueAt !== this.getTaskByDuidIfLoaded(e.duid)?.dueAt)
          .map(({ duid, startAt, dueAt }) => [duid, { startAt: startAt ?? undefined, dueAt: dueAt ?? undefined }])
      );

      updates.forEach((update) => {
        const task = this.getTaskByDuidIfLoaded(update.duid);
        if (!task) {
          return;
        }

        const draftRemoved = task.drafterDuid !== null && update.drafterDuid === null;
        const trashRemoved = task.inTrash === true && update.inTrash === false;

        if ((draftRemoved || trashRemoved) && !newlyUpdatedDuidToDueAt.has(update.duid)) {
          if (task.dueAt) {
            newlyUpdatedDuidToDueAt.set(update.duid, {
              startAt: task.startAt ?? undefined,
              dueAt: task.dueAt ?? undefined,
            });
          }
        }
      });

      const originalUpdateDuidsList = new Set(newlyUpdatedDuidToDueAt.keys());
      const dateUpdatedTaskDuids = new Set(
        updates.filter((e) => e.startAt !== undefined || e.dueAt !== undefined).map((e) => e.duid)
      );
      const queue = this.getTasksByDuidsIfLoaded([...originalUpdateDuidsList], {
        includeTrashed: false,
        includeDraft: false,
      });
      while (queue.length !== 0) {
        const curr = queue.shift() as Task;
        const dueAt = newlyUpdatedDuidToDueAt.get(curr.duid)?.dueAt;
        if (!dueAt) {
          continue;
        }

        this.getTasksByDuidsOrderedIfLoaded(
          this.getRelationshipsByKindKind(curr, RelationshipKindKind.BLOCKS, true).map((e) => e.targetDuid),
          { includeTrashed: false, includeDraft: false }
        ).forEach((blockee) => {
          if (dateUpdatedTaskDuids.has(blockee.duid)) {
            return;
          }
          const currentStartDueAt = newlyUpdatedDuidToDueAt.get(blockee.duid);
          const currentStartAt = currentStartDueAt?.startAt ?? blockee.startAt;
          const currentDueAt = currentStartDueAt?.dueAt ?? blockee.dueAt;
          if (!currentStartAt) {
            if (currentDueAt && currentDueAt < dueAt) {
              newlyUpdatedDuidToDueAt.set(blockee.duid, { startAt: undefined, dueAt });
              queue.push(blockee);
            }
            return;
          }
          if (currentStartAt > dueAt) {
            return;
          }
          const newDueAt = currentDueAt
            ? formatDate(moment(dueAt).add(moment(currentDueAt).diff(currentStartAt, "day"), "day"))
            : undefined;
          newlyUpdatedDuidToDueAt.set(blockee.duid, { startAt: dueAt, dueAt: newDueAt });
          if (newDueAt) {
            queue.push(blockee);
          }
        });
      }

      automatedUpdates.push(
        ...[...newlyUpdatedDuidToDueAt.entries()]
          .filter(([duid]) => !originalUpdateDuidsList.has(duid))
          .map(([duid, e]) => ({ duid, ...e }))
      );
    }
    updates.push(...automatedUpdates.map((e) => ({ ...e, automatedUpdate: true })));

    const subscriberListAdds: TaskUpdate[] = [];
    const inverseUpdates: TaskUpdate[] = [];

    await this.getTasksByDuids(
      updates.map((e) => e.duid),
      { includeTrashed: false, includeDraft: true }
    );
    updates.forEach((update) => {
      const task = this.getTaskByDuidIfLoaded(update.duid);
      if (!task) {
        return;
      }
      const oldTask = { ...task };

      if (overrideFilters) {
        removeFiltersThatDoNotPassForTask(this, { ...task, ...update });
      }

      const subscriberDuids = this.$updateTaskSubscribersNoBackend(task, [updaterDuid]);
      if (subscriberDuids.length > 0) {
        subscriberListAdds.push({ duid: task.duid, subscriberDuids });
      }

      const inverseUpdate: TaskUpdate = { duid: task.duid };

      // oxlint-disable-next-line dart/restricted-syntax
      for (const [field, value] of Object.entries(update)) {
        if (field === "duid") {
          continue;
        }
        if (field === "inTrash" && value === true) {
          appStore.selectedTaskDuids.delete(task.duid);
        }
        if (
          !noCelebrate &&
          field === "statusDuid" &&
          this.getStatusByDuid(value as string)?.kind === StatusKind.FINISHED &&
          (this.getStatusByDuid(task.statusDuid)?.kind !== StatusKind.FINISHED || celebrateOverride)
        ) {
          firework();
        }

        const fieldAsKeyOfTask = field as keyof Task;
        (inverseUpdate[field as keyof TaskUpdate] as unknown) = task[fieldAsKeyOfTask];
        (task[fieldAsKeyOfTask] as unknown) = value;
      }

      const shouldRemoveForTrashChange = task.inTrash !== undefined && task.inTrash === update.inTrash;
      const isDartboardChange = !!update.dartboardDuid && update.dartboardDuid !== oldTask.dartboardDuid;
      const shouldResetPaginationMembership = shouldRemoveForTrashChange || isDartboardChange;

      if (shouldResetPaginationMembership) {
        this.$updateSummaryStatisticsForTaskChange(oldTask, task);
        this.$removeEntitiesFromPaginationStates([task]);
      }

      if (isDartboardChange) {
        this.$updateDartboardTopTaskOrderNoBackend(oldTask.dartboardDuid, task, oldTask.order);
        this.$updateDartboardTopTaskOrderNoBackend(task.dartboardDuid, task, undefined);
      } else {
        this.$updateDartboardTopTaskOrderNoBackend(task.dartboardDuid, task, oldTask.order);
      }

      inverseUpdates.push(inverseUpdate);
      task.updatedAt = new Date().toISOString();
      task.updatedByDuid = updaterDuid;

      if (update.statusDuid && update.statusDuid !== oldTask.statusDuid) {
        const newStatus = this.getStatusByDuid(task.statusDuid);
        if (newStatus && COMPLETED_STATUS_KINDS.has(newStatus.kind)) {
          if (task.completedAt === null) {
            task.completedAt = task.updatedAt;
          }
        } else {
          task.completedAt = null;
        }
      }

      if (!shouldResetPaginationMembership) {
        this.$updateSummaryStatisticsForTaskChange(oldTask, task);
      }
      this.$updateRollupForTaskUpdatesUiOnly(task, oldTask);
      this.$maybeRemoveInvalidEntityFromPaginationStates(task);

      // Also re-validate all ancestors since they might be absentee parents that no longer qualify
      const ancestorDuids = this.getAncestorDuidsUiOnly(task);
      const ancestors = this.getTasksByDuidsIfLoaded(ancestorDuids, { includeTrashed: false, includeDraft: false });
      ancestors.forEach((ancestor) => {
        this.$maybeRemoveInvalidEntityFromPaginationStates(ancestor);
      });

      if (!task.drafterDuid) {
        this.$addEntitiesToPaginationStates(TASK_PAGE_PAGINATION_KEY, [task]);
      }

      userStore.addRecentTask(task);

      if (this.$isPrimaryDraft(task)) {
        this.taskDraftDuid = task.duid;
      } else if (task.duid === this.taskDraft?.duid) {
        this.taskDraftDuid = null;
        userStore.updateTutorialStatuses([{ name: TutorialName.MAKE_A_TASK, status: 2 }], false, true);
      }

      const suggestedUpdate = checkTaskConstraints(update, this);
      if (Object.keys(suggestedUpdate).length > 0) {
        Object.assign(update, suggestedUpdate);
        printOrCaptureMessage("Task constraints violated");
      }
    });

    deletes.forEach((task) => {
      const subscriberDuids = this.$updateTaskSubscribersNoBackend(task, [updaterDuid]);
      if (subscriberDuids.length > 0) {
        subscriberListAdds.push({ duid: task.duid, subscriberDuids });
      }

      this.$updateSummaryStatisticsForTaskChange(task, undefined);
      this.$removeEntitiesFromPaginationStates([task]);
      this.$deleteTask(task);
    });

    if (!noBackend) {
      const backendAction = this.$backend.task.updateUpdateListAddAndDeleteMany(
        updates.filter((e) => !("automatedUpdate" in e) || !e.automatedUpdate),
        deletes.map((e) => e.duid),
        subscriberListAdds
      );

      deletes.forEach((e) => this.$addEntityDuidPendingDeletion(e.duid));
      const action = (async () => {
        await backendAction;
        deletes.forEach((e) => this.$removeEntityDuidPendingDeletion(e.duid));
      })();

      if (awaitBackend) {
        await action;
      }
    }

    if (!noUndo) {
      const fields = [...new Set(updates.flatMap((e) => Object.keys(e)))].filter((e) => e !== "duid");
      const field = fields.length === 0 ? fields[0] : undefined;

      await this.getTasksByDuids(
        updates.map((e) => e.duid),
        { includeTrashed: false, includeDraft: true }
      );
      const tasks = [...new Set(updates.map((e) => e.duid))]
        .map((e) => this.getTaskByDuidIfLoaded(e))
        .filter(isDefined);
      const content = tasks.length === 1 ? tasks[0].title : getItemCountText(tasks.length, "task");
      const makeDescriptionNorm = makeDescription ?? makeDefaultMakeDescription(field, tasks.length);
      const descriptionPast = makeDescriptionNorm(false, content);
      const descriptionActive = makeDescriptionNorm(true, content);

      this.saveToUndo(
        {
          modelType: ModelType.TASK,
          duid: updates.map((u) => u.duid),
          field,
        },
        [descriptionPast, descriptionActive],
        () => this.updateTasks(inverseUpdates, { noUndo: true, noBackend }),
        () => this.updateTasks(updates, { noUndo: true, noBackend })
      );
    }
  },
  async addUsers(property, taskDuids, userDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const { isDefault } = getPropertyConfig(property.kind);
    const isDefaultAssignee = property.kind === PropertyKind.DEFAULT_ASSIGNEES;
    const updaterDuid = userStore.duid;

    const taskUpdates: TaskUpdate[] = [];
    const subscriberListAdds: TaskUpdate[] = [];

    const tasks = await this.getTasksByDuids(taskDuids, { includeTrashed: false, includeDraft: true });
    tasks.forEach((task) => {
      const oldTask = { ...task };
      const currentUserDuids = getPropertyValueFromTask(property, task);
      const userDuidsOrdered = this.getUsersByDuidsOrdered([...new Set(userDuids)]).map((e) => e.duid);
      const newUserDuidsOrdered = this.getUsersByDuidsOrdered([...new Set([...currentUserDuids, ...userDuids])]).map(
        (e) => e.duid
      );
      const partialTaskUpdate = getPropertyPartialTask(property, task, newUserDuidsOrdered);
      Object.assign(task, partialTaskUpdate);
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      userStore.addRecentTask(task);

      const taskUpdate: TaskUpdate = {
        duid: task.duid,
        ...(isDefault ? getPropertyPartialTask(property, task, userDuidsOrdered) : partialTaskUpdate),
      };

      const subscriberDuids = this.$updateTaskSubscribersNoBackend(task, [updaterDuid, ...userDuids]);
      if (subscriberDuids.length > 0) {
        subscriberListAdds.push({ duid: task.duid, subscriberDuids });
      }

      taskUpdates.push(taskUpdate);

      this.$updateSummaryStatisticsForTaskChange(oldTask, task);
      this.$maybeRemoveInvalidEntityFromPaginationStates(task);
      if (!task.drafterDuid) {
        this.$addEntitiesToPaginationStates(TASK_PAGE_PAGINATION_KEY, [task]);
      }
    });

    let backendAction: Promise<TransactionResponse[]>;

    if (isDefault) {
      backendAction = this.$backend.task.updateListAddMany([...taskUpdates, ...subscriberListAdds]);
    } else {
      backendAction = this.$backend.task.updateAndUpdateListAddMany(taskUpdates, subscriberListAdds);
    }

    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const addedUsersStr = prettyFormatList(this.getUsersByDuidsOrdered(userDuids).map((e) => getUserDisplayName(e)));

      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: property.duid },
        [
          `${isDefaultAssignee ? "Assigned" : `Added ${getPropertyDisplayTitle(property).toLowerCase()}`} ${addedUsersStr} to ${content}`,
          `${isDefaultAssignee ? "assigning" : `adding ${getPropertyDisplayTitle(property).toLowerCase()}`} ${addedUsersStr} to ${content}`,
        ],
        () => this.removeUsers(property, taskDuids, userDuids, { noUndo: true }),
        () => this.addUsers(property, taskDuids, userDuids, { noUndo: true })
      );
    }
  },
  async removeUsers(property, taskDuids, userDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const { isDefault } = getPropertyConfig(property.kind);
    const isDefaultAssignee = property.kind === PropertyKind.DEFAULT_ASSIGNEES;
    const updaterDuid = userStore.duid;
    const tasks = await this.getTasksByDuids(taskDuids, { includeTrashed: false, includeDraft: true });

    const taskUpdates: TaskUpdate[] = [];
    tasks.forEach((task) => {
      const oldTask = { ...task };
      const currentUserDuids = getPropertyValueFromTask(property, task);
      const userDuidsOrdered = this.getUsersByDuidsOrdered([...new Set(userDuids)]).map((e) => e.duid);
      const partialTaskUpdate = getPropertyPartialTask(
        property,
        task,
        currentUserDuids.filter((e) => !userDuids.includes(e))
      );
      Object.assign(task, partialTaskUpdate);
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      userStore.addRecentTask(task);

      taskUpdates.push({
        duid: task.duid,
        ...(isDefault ? getPropertyPartialTask(property, task, userDuidsOrdered) : partialTaskUpdate),
      });

      this.$updateSummaryStatisticsForTaskChange(oldTask, task);
      this.$maybeRemoveInvalidEntityFromPaginationStates(task);
      if (!task.drafterDuid) {
        this.$addEntitiesToPaginationStates(TASK_PAGE_PAGINATION_KEY, [task]);
      }
    });

    let backendAction: Promise<TransactionResponse[]>;
    // TODO subscribe updater
    if (isDefault) {
      backendAction = this.$backend.task.updateListRemoveMany(taskUpdates);
    } else {
      backendAction = this.$backend.task.updateMany(taskUpdates);
    }

    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const removedUsersStr = prettyFormatList(
        this.getUsersByDuidsOrdered(userDuids).map((e) => getUserDisplayName(e))
      );

      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: property.duid },
        [
          `${isDefaultAssignee ? "Unassigned" : `Removed ${getPropertyDisplayTitle(property).toLowerCase()}`} ${removedUsersStr} from ${content}`,
          `${
            isDefaultAssignee ? "unassigning" : `removing ${getPropertyDisplayTitle(property).toLowerCase()}`
          } ${removedUsersStr} from ${content}`,
        ],
        () => this.addUsers(property, taskDuids, userDuids, { noUndo: true }),
        () => this.removeUsers(property, taskDuids, userDuids, { noUndo: true })
      );
    }
  },
  async replaceUsers(property, taskDuids, userDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;

    const isDefaultAssignee = property.kind === PropertyKind.DEFAULT_ASSIGNEES;
    const userStore = this.$useUserStore();

    const updaterDuid = userStore.duid;

    const taskUpdates: TaskUpdate[] = [];
    const subscriberListAdds: TaskUpdate[] = [];

    const oldUserDuids: string[] = [];

    const tasks = await this.getTasksByDuids(taskDuids, { includeTrashed: false, includeDraft: true });
    tasks.forEach((task) => {
      const oldTask = { ...task };
      const currentUserDuids = getPropertyValueFromTask(property, task);
      oldUserDuids.push(...currentUserDuids);
      const newUserDuidsOrdered = this.getUsersByDuidsOrdered([...new Set(userDuids)]).map((e) => e.duid);
      const partialTaskUpdate = getPropertyPartialTask(property, task, newUserDuidsOrdered);
      Object.assign(task, partialTaskUpdate);
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      userStore.addRecentTask(task);

      const subscriberDuids = this.$updateTaskSubscribersNoBackend(task, [updaterDuid, ...userDuids]);
      if (subscriberDuids.length > 0) {
        subscriberListAdds.push({ duid: task.duid, subscriberDuids });
      }

      taskUpdates.push({ duid: task.duid, ...partialTaskUpdate });

      this.$updateSummaryStatisticsForTaskChange(oldTask, task);
      this.$maybeRemoveInvalidEntityFromPaginationStates(task);
      if (!task.drafterDuid) {
        this.$addEntitiesToPaginationStates(TASK_PAGE_PAGINATION_KEY, [task]);
      }
    });

    const backendAction = this.$backend.task.updateAndUpdateListAddMany(taskUpdates, subscriberListAdds);
    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const addedUsersStr = prettyFormatList(this.getUsersByDuidsOrdered(userDuids).map((e) => getUserDisplayName(e)));
      const pastAction = userDuids
        ? `${isDefaultAssignee ? "Assigned" : `Added ${getPropertyDisplayTitle(property).toLowerCase()}`} ${addedUsersStr} to`
        : isDefaultAssignee
          ? "Unassigned"
          : "Removed";
      const presentAction = userDuids
        ? `${isDefaultAssignee ? "assigning" : `adding ${getPropertyDisplayTitle(property).toLowerCase()}`} ${addedUsersStr} to`
        : isDefaultAssignee
          ? "unassigning"
          : "removing";
      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: property.duid },
        [`${pastAction} ${content}`, `${presentAction} ${content}`],
        () => this.replaceUsers(property, taskDuids, oldUserDuids, { noUndo: true }),
        () => this.replaceUsers(property, taskDuids, userDuids, { noUndo: true })
      );
    }
  },
  async addSubscribers(taskDuids, subscriberDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const updaterDuid = userStore.duid;

    const tasks = await this.getTasksByDuids(taskDuids, { includeTrashed: false, includeDraft: true });

    const taskUpdates: TaskUpdate[] = [];
    tasks.forEach((task) => {
      // eslint-disable-next-line no-param-reassign
      task.subscriberDuids = [...new Set([...task.subscriberDuids, ...subscriberDuids])];
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      userStore.addRecentTask(task);

      taskUpdates.push({ duid: task.duid, subscriberDuids });
    });

    const backendAction = this.$backend.task.updateListAddMany(taskUpdates);
    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const addedSubscribersStr = prettyFormatList(
        this.getUsersByDuidsOrdered(subscriberDuids).map((e) => getUserDisplayName(e))
      );
      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: "subscriberDuids" },
        [`Subscribed ${addedSubscribersStr} to ${content}`, `subscribing ${addedSubscribersStr} to ${content}`],
        () => this.removeSubscribers(taskDuids, subscriberDuids, { noUndo: true }),
        () => this.addSubscribers(taskDuids, subscriberDuids, { noUndo: true })
      );
    }
  },
  async removeSubscribers(taskDuids, subscriberDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const updaterDuid = userStore.duid;

    const tasks = await this.getTasksByDuids(taskDuids, { includeTrashed: false, includeDraft: true });

    const taskUpdates: TaskUpdate[] = [];
    tasks.forEach((task) => {
      // eslint-disable-next-line no-param-reassign
      task.subscriberDuids = task.subscriberDuids.filter((e) => !subscriberDuids.includes(e));
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      userStore.addRecentTask(task);

      taskUpdates.push({ duid: task.duid, subscriberDuids });
    });

    const backendAction = this.$backend.task.updateListRemoveMany(taskUpdates);
    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const removedSubscribersStr = prettyFormatList(
        this.getUsersByDuidsOrdered(subscriberDuids).map((e) => getUserDisplayName(e))
      );
      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: "subscriberDuids" },
        [
          `Unsubscribed ${removedSubscribersStr} from ${content}`,
          `unsubscribing ${removedSubscribersStr} from ${content}`,
        ],
        () => this.addSubscribers(taskDuids, subscriberDuids, { noUndo: true }),
        () => this.removeSubscribers(taskDuids, subscriberDuids, { noUndo: true })
      );
    }
  },
  async addOptions(property, taskDuids, optionDuids, options = {}) {
    const { noUndo = false, createTitle, awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const { isDefault } = getPropertyConfig(property.kind);
    const updaterDuid = userStore.duid;

    let createdOption: Option | undefined;
    if (createTitle) {
      createdOption = this.$createOptionNoBackend(createTitle, property);
      if (createdOption) {
        optionDuids.push(createdOption.duid);
      }
    }

    const taskUpdates: TaskUpdate[] = [];
    const tasks = await this.getTasksByDuids(taskDuids, { includeTrashed: false, includeDraft: true });
    tasks.forEach((task) => {
      const oldTask = { ...task };

      // Sort options alphabetically
      const currentOptionDuids = getPropertyValueFromTask(property, task);
      const newDuids = [...currentOptionDuids, ...optionDuids];

      const partialTaskUpdate = getPropertyPartialTask(
        property,
        task,
        this.getOptionsByDuidsOrdered(newDuids, property).map((e) => e.duid)
      );
      Object.assign(task, partialTaskUpdate);
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      userStore.addRecentTask(task);

      taskUpdates.push({
        duid: task.duid,
        ...(isDefault ? { tagDuids: optionDuids } : partialTaskUpdate),
      });

      this.$updateSummaryStatisticsForTaskChange(oldTask, task);
      this.$maybeRemoveInvalidEntityFromPaginationStates(task);
      if (!task.drafterDuid) {
        this.$addEntitiesToPaginationStates(TASK_PAGE_PAGINATION_KEY, [task]);
      }
    });

    // TODO subscribe updater

    let transactionResponse: Promise<TransactionResponse[]>;
    if (isDefault) {
      if (createdOption) {
        transactionResponse = this.$backend.option.createAndTaskUpdateListAddMany(createdOption, taskUpdates);
      } else {
        transactionResponse = this.$backend.task.updateListAddMany(taskUpdates);
      }
    } else if (createdOption) {
      transactionResponse = this.$backend.option.createAndTaskUpdateMany(createdOption, taskUpdates);
    } else {
      transactionResponse = this.$backend.task.updateMany(taskUpdates);
    }

    if (awaitBackend) {
      await transactionResponse;
    }

    if (!noUndo) {
      const addedOptionsStr = prettyFormatList(
        this.getOptionsByDuidsOrdered(optionDuids, property).map((t) => t.title)
      );
      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: property.duid },
        [
          `Added ${getPropertyDisplayTitle(property).toLowerCase()} ${addedOptionsStr} to ${content}`,
          `adding  ${getPropertyDisplayTitle(property).toLowerCase()} ${addedOptionsStr} to ${content}`,
        ],
        () => this.removeOptions(property, taskDuids, optionDuids, { noUndo: true }),
        () => this.addOptions(property, taskDuids, optionDuids, { noUndo: true })
      );
    }
    return transactionResponse;
  },
  async removeOptions(property, taskDuids, optionDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const { isDefault } = getPropertyConfig(property.kind);
    const updaterDuid = userStore.duid;
    const tasks = await this.getTasksByDuids(taskDuids, { includeTrashed: false, includeDraft: true });

    const taskUpdates: TaskUpdate[] = [];
    tasks.forEach((task) => {
      const oldTask = { ...task };

      const currentOptionDuids = getPropertyValueFromTask(property, task);
      const partialTaskUpdate = getPropertyPartialTask(
        property,
        task,
        currentOptionDuids.filter((e) => !optionDuids.includes(e))
      );
      Object.assign(task, partialTaskUpdate);
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      userStore.addRecentTask(task);

      taskUpdates.push({ duid: task.duid, ...(isDefault ? { tagDuids: optionDuids } : partialTaskUpdate) });

      this.$updateSummaryStatisticsForTaskChange(oldTask, task);
      this.$maybeRemoveInvalidEntityFromPaginationStates(task);
      if (!task.drafterDuid) {
        this.$addEntitiesToPaginationStates(TASK_PAGE_PAGINATION_KEY, [task]);
      }
    });

    // TODO subscribe updater
    let backendAction: Promise<TransactionResponse[]>;

    if (isDefault) {
      backendAction = this.$backend.task.updateListRemoveMany(taskUpdates);
    } else {
      backendAction = this.$backend.task.updateMany(taskUpdates);
    }

    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const removedOptionsStr = prettyFormatList(
        this.getOptionsByDuidsOrdered(optionDuids, property).map((t) => t.title)
      );
      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: property.duid },
        [
          `Removed ${getPropertyDisplayTitle(property).toLowerCase()} ${removedOptionsStr} from ${content}`,
          `removing ${getPropertyDisplayTitle(property).toLowerCase()} ${removedOptionsStr} from ${content}`,
        ],
        () => this.addOptions(property, taskDuids, optionDuids, { noUndo: true }),
        () => this.removeOptions(property, taskDuids, optionDuids, { noUndo: true })
      );
    }
  },
  async replaceOptions(property, taskDuids, optionDuids, options = {}) {
    const { noUndo = false, awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const updaterDuid = userStore.duid;
    const tasks = await this.getTasksByDuids(taskDuids, { includeTrashed: false, includeDraft: true });

    const taskUpdates: TaskUpdate[] = [];
    const oldOptionDuids: string[] = [];
    tasks.forEach((task) => {
      const oldTask = { ...task };

      // Sort options alphabetically
      const currentOptionDuids = getPropertyValueFromTask(property, task);
      oldOptionDuids.push(...currentOptionDuids);

      const partialTaskUpdate = getPropertyPartialTask(
        property,
        task,
        this.getOptionsByDuidsOrdered([...new Set(optionDuids)], property).map((e) => e.duid)
      );
      Object.assign(task, partialTaskUpdate);
      // eslint-disable-next-line no-param-reassign
      task.updatedAt = new Date().toISOString();
      // eslint-disable-next-line no-param-reassign
      task.updatedByDuid = updaterDuid;

      userStore.addRecentTask(task);

      taskUpdates.push({ duid: task.duid, ...partialTaskUpdate });

      this.$updateSummaryStatisticsForTaskChange(oldTask, task);
      this.$maybeRemoveInvalidEntityFromPaginationStates(task);
      if (!task.drafterDuid) {
        this.$addEntitiesToPaginationStates(TASK_PAGE_PAGINATION_KEY, [task]);
      }
    });

    // TODO subscribe updater

    const backendAction = this.$backend.task.updateMany(taskUpdates);
    if (awaitBackend) {
      await backendAction;
    }

    if (!noUndo) {
      const addedOptionsStr = prettyFormatList(
        this.getOptionsByDuidsOrdered(optionDuids, property).map((t) => t.title)
      );
      const content = tasks.length > 1 ? getItemCountText(tasks.length, "task") : `task ${tasks[0]?.title ?? ""}`;
      this.saveToUndo(
        { modelType: ModelType.TASK, duid: taskDuids, field: property.duid },
        [
          `Set ${getPropertyDisplayTitle(property).toLowerCase()} ${addedOptionsStr} on ${content}`,
          `setting  ${getPropertyDisplayTitle(property).toLowerCase()} ${addedOptionsStr} on ${content}`,
        ],
        () => this.replaceOptions(property, taskDuids, oldOptionDuids, { noUndo: true }),
        () => this.replaceOptions(property, taskDuids, optionDuids, { noUndo: true })
      );
    }
  },
  async createRelationship(sourceDuid, targetDuid, relationshipKindDuid, options = {}) {
    const { noBackend = false, awaitBackend = false } = options;

    if (sourceDuid === targetDuid) {
      return undefined;
    }
    const sourceTask = this.getTaskByDuidIfLoaded(sourceDuid);
    const targetTask = this.getTaskByDuidIfLoaded(targetDuid);
    const relationshipKind = this.getRelationshipKindByDuid(relationshipKindDuid);
    if (!sourceTask || !targetTask || !relationshipKind) {
      return undefined;
    }
    const preexistingRelationship = sourceTask.relationships.filter(
      (e) => e.targetDuid === targetDuid && e.kindDuid === relationshipKindDuid
    )[0];
    if (preexistingRelationship && preexistingRelationship.isForward) {
      return preexistingRelationship;
    }

    const relatedTaskDuids = this.getSourceOrTargetDuidsUiOnly(targetTask, relationshipKind.kind, false, true);
    if (relatedTaskDuids.some((e) => e === sourceDuid)) {
      printOrCaptureMessage("Relationship would create a cycle", {
        sourceDuid,
        targetDuid,
        relationshipKind: relationshipKind.kind,
      });
      return undefined;
    }

    const duid = makeDuid();
    const forwardRelationship = this.$createRelationshipOneDirectionNoBackend(
      duid,
      sourceDuid,
      targetDuid,
      relationshipKindDuid,
      relationshipKind.directed ? true : undefined
    );
    const backwardRelationship = this.$createRelationshipOneDirectionNoBackend(
      duid,
      targetDuid,
      sourceDuid,
      relationshipKindDuid,
      relationshipKind.directed ? false : undefined
    );
    if (!forwardRelationship || !backwardRelationship) {
      return undefined;
    }

    this.$useUserStore().addRecentTask(sourceTask);
    this.$useUserStore().addRecentTask(targetTask);

    // TODO subscribe updater to both tasks

    if (!noBackend) {
      let backendAction: Promise<TransactionResponse[]>;
      const createObj = { ...forwardRelationship, sourceDuid };
      if (preexistingRelationship) {
        this.deleteRelationship(sourceDuid, preexistingRelationship.duid, { noBackend: true });
        backendAction = this.$backend.relationship.deleteAndCreate(preexistingRelationship.duid, createObj);
      } else {
        backendAction = (async () => {
          const response = await this.$backend.relationship.create(createObj);
          return [response];
        })();
      }

      if (awaitBackend) {
        await backendAction;
      }
    }

    return forwardRelationship;
  },
  async deleteRelationship(sourceDuid, relationshipDuid, options = {}) {
    const { noBackend = false, awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const now = new Date().toISOString();
    const updaterDuid = userStore.duid;

    const sourceTask = await this.getTaskByDuid(sourceDuid);
    if (!sourceTask) {
      return;
    }
    const relationship = sourceTask.relationships.find((e) => e.duid === relationshipDuid);
    if (!relationship) {
      return;
    }
    const targetTask = await this.getTaskByDuid(relationship.targetDuid);
    if (!targetTask) {
      return;
    }

    const parentKindDuid = this.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;
    const isParentRelationship = relationship.kindDuid === parentKindDuid;
    if (isParentRelationship) {
      if (relationship.isForward) {
        this.$addOrRemoveRollupUiOnly(targetTask, { remove: true });
      } else {
        this.$addOrRemoveRollupUiOnly(sourceTask, { remove: true });
      }
    }

    sourceTask.relationships = sourceTask.relationships.filter((e) => e.duid !== relationshipDuid);
    sourceTask.updatedAt = now;
    sourceTask.updatedByDuid = updaterDuid;
    targetTask.relationships = targetTask.relationships.filter((e) => e.duid !== relationshipDuid);
    targetTask.updatedAt = now;
    targetTask.updatedByDuid = updaterDuid;

    userStore.addRecentTask(sourceTask);
    userStore.addRecentTask(targetTask);

    // TODO subscribe updater to both tasks

    let backendAction: Promise<TransactionResponse> | undefined;
    if (!noBackend) {
      backendAction = this.$backend.relationship.delete(relationshipDuid);
    }

    if (awaitBackend && backendAction) {
      await backendAction;
    }
  },
  async deleteAndCreateRelationshipAndUpdateTask(relationshipDelete, relationshipCreate, taskUpdate, options = {}) {
    if (!relationshipDelete && !relationshipCreate && !taskUpdate) {
      return;
    }

    const {
      noBackend = false,
      celebrateOverride = false,
      awaitBackend = false,
      onRemapFailed = () => undefined,
    } = options;

    let deleteDuid: string | undefined;
    let relationshipCreateFormal: RelationshipCreate | undefined;

    if (relationshipDelete) {
      const { taskDuid, relationshipDuid } = relationshipDelete;
      this.deleteRelationship(taskDuid, relationshipDuid, { noBackend: true, awaitBackend });
      deleteDuid = relationshipDuid;
    }

    if (relationshipCreate) {
      const { sourceDuid, targetDuid, relationshipKindDuid } = relationshipCreate;
      const forwardRelationship = await this.createRelationship(sourceDuid, targetDuid, relationshipKindDuid, {
        noBackend: true,
      });
      if (forwardRelationship) {
        relationshipCreateFormal = { ...forwardRelationship, sourceDuid };
      }
    }

    if (taskUpdate) {
      this.updateTasks([taskUpdate], { noUndo: true, noBackend, celebrateOverride, awaitBackend, onRemapFailed });
    }

    // TODO subscribe updater to all (up to 5) tasks

    if (!noBackend) {
      if (deleteDuid || relationshipCreateFormal) {
        const backendAction = this.$backend.relationship.deleteAndCreateAndUpdateTask(
          deleteDuid,
          relationshipCreateFormal
        );

        if (awaitBackend) {
          await backendAction;
        }
      }
    }
  },
  async addAttachments(taskDuid, files, options = {}) {
    const { awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const task = this.getTaskByDuidIfLoaded(taskDuid, { assertLoaded: true });
    if (!task) {
      return;
    }

    const lastAttachmentDuid = task.attachmentDuids.at(-1);
    const lastAttachment = lastAttachmentDuid
      ? this.getAttachmentByDuidIfLoaded(lastAttachmentDuid, { assertLoaded: true })
      : undefined;
    const orders = getOrdersBetween(lastAttachment?.order, undefined, files.length);
    const attachmentConfigs = files.map((file, i) => ({
      file,
      order: orders[i],
      partialAttachment: { duid: makeDuid() },
    }));
    const { readableFiles } = await partitionReadableFiles(attachmentConfigs);

    const attachmentDuids = readableFiles.map((e) => e.partialAttachment?.duid).filter(isDefined);
    if (attachmentDuids.length === 0) {
      return;
    }

    task.attachmentDuids.push(...attachmentDuids);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = userStore.duid;

    this.$useUserStore().addRecentTask(task);

    // TODO subscribe updater

    const attachmentAndCreates = await Promise.all(
      readableFiles.map(async ({ file, order, partialAttachment }) =>
        this.createAttachment(file, order, partialAttachment, { taskDuid, noBackend: true })
      )
    );

    const taskUpdate: TaskUpdate = {
      duid: taskDuid,
      attachmentDuids,
    };

    const backendAction = this.$backend.task.createAttachmentsAndAdd(
      attachmentAndCreates.map((e) => e.create),
      taskUpdate
    );

    if (awaitBackend) {
      await backendAction;
    }
  },
  async removeAttachment(taskDuid, attachmentDuid, options = {}) {
    const { awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const task = this.getTaskByDuidIfLoaded(taskDuid, { assertLoaded: true });
    const attachment = this.getAttachmentByDuidIfLoaded(attachmentDuid, { assertLoaded: true });
    if (!task || !attachment) {
      return;
    }

    task.attachmentDuids = task.attachmentDuids.filter((e) => e !== attachmentDuid);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = userStore.duid;

    userStore.addRecentTask(task);

    // TODO subscribe updater

    const taskUpdate: TaskUpdate = {
      duid: taskDuid,
      attachmentDuids: [attachmentDuid],
    };
    this.$addEntityDuidPendingDeletion(attachment.duid);
    const backendAction = this.$backend.task.removeAttachmentAndDelete(taskUpdate, attachmentDuid);

    const action = (async () => {
      await backendAction;
      this.$removeEntityDuidPendingDeletion(attachment.duid);
    })();

    if (awaitBackend) {
      await action;
    }
  },
  async addLink(taskDuid, kind, url, title, options = {}) {
    const { awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return;
    }
    const allOrders = task.links.map((e) => e.order).sort(stringComparator);
    const order = getOrdersBetween(allOrders[allOrders.length - 1], undefined)[0];

    const taskLinkCreate = { duid: makeDuid(), taskDuid: task.duid, kind, order, url, title };

    this.$addLinksNoBackend(task, [taskLinkCreate]);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = userStore.duid;

    userStore.addRecentTask(task);

    // TODO subscribe updater

    const backendAction = this.$backend.task.link.create(taskLinkCreate);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async updateLink(taskDuid, linkUpdate, options = {}) {
    const { awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return;
    }

    this.$updateLinksNoBackend(task, [linkUpdate]);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = userStore.duid;

    userStore.addRecentTask(task);

    const backendAction = this.$backend.task.link.update(linkUpdate);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async deleteLink(taskDuid, linkDuid, options = {}) {
    const { awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return;
    }

    this.$deleteLinksNoBackend(task, [linkDuid]);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = userStore.duid;

    userStore.addRecentTask(task);

    // TODO subscribe updater

    const backendAction = this.$backend.task.link.delete(linkDuid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async addUpdateAndDeleteLinks(taskDuid, creates, updates, deletes, options = {}) {
    const { awaitBackend = false } = options;
    const userStore = this.$useUserStore();

    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return;
    }

    this.$addLinksNoBackend(task, creates);
    this.$updateLinksNoBackend(task, updates);
    this.$deleteLinksNoBackend(task, deletes);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = userStore.duid;

    userStore.addRecentTask(task);

    // TODO subscribe updater to all tasks

    const backendAction = this.$backend.task.link.addUpdateAndDeleteMany(creates, updates, deletes);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async $replicateTaskRecursive(task, order, partialTask, options = {}) {
    const {
      propertiesAndParentOnly = false,
      includeDuplicate = false,
      maintainTitle = false,
      maintainStatus = false,
      resetSize = false,
    } = options;
    const userStore = this.$useUserStore();

    const res = {
      taskCreates: [] as TaskCreate[],
      relationshipCreates: [] as RelationshipCreate[],
      taskDocRelationshipCreates: [] as TaskDocRelationshipCreate[],
      linkCreates: [] as TaskLinkCreate[],
      attachmentCreates: [] as AttachmentCreate[],
    };

    const parentKindDuid = this.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;
    const duplicateKindDuid = this.getRelationshipKindByKind(RelationshipKindKind.DUPLICATES).duid;

    // Add the task itself
    const now = new Date().toISOString();
    const replicatedTask: Task = {
      ...deepCopy(task),
      duid: makeDuid(),
      createdAt: now,
      updatedAt: now,
      order,
      recommendationDuid: null,
      subscriberDuids: [...new Set([...task.subscriberDuids, userStore.duid])].sort(),
      links: [],
      attachmentDuids: [],
      relationships: [],
      docRelationships: [],
      timeTracking: [],
      completedAt: null,
      recurrence: null,
      recursNextAt: null,
      ...partialTask,
    };
    if (!maintainTitle && partialTask?.title === undefined) {
      replicatedTask.title = getNextTitleInSequence(
        task.title,
        this.getLoadedTasksForPageDuidOrdered(replicatedTask.dartboardDuid).map((e) => e.title)
      );
    }
    if (resetSize && partialTask?.size === undefined) {
      replicatedTask.size = null;
    }
    if (!maintainStatus) {
      if (partialTask?.statusDuid === undefined) {
        replicatedTask.statusDuid = this.defaultDefaultUnstartedStatus.duid;
      }
      const partialPropertyOverrides = partialTask?.properties ?? {};
      const customStatusProperties = this.getPropertyByKind(PropertyKind.STATUS);
      customStatusProperties
        .filter((property) => partialPropertyOverrides[property.duid] === undefined)
        .forEach((property) => delete replicatedTask.properties[property.duid]);
    }
    replicatedTask.relationships = replicatedTask.relationships.map((e) => ({
      ...e,
      duid: makeDuid(),
      sourceDuid: replicatedTask.duid,
    }));

    this.$createOrUpdateTask(replicatedTask);

    removeFiltersThatDoNotPassForTask(this, replicatedTask);

    res.taskCreates.push({
      ...replicatedTask,
      rollup: undefined,
      sourceKind: TaskSourceKind.APP_REPLICATE,
      sourceTaskDuid: task.duid,
    });

    res.relationshipCreates.push(
      ...replicatedTask.relationships
        .map(({ duid, kindDuid, targetDuid, isForward }) => [
          this.$createRelationshipOneDirectionNoBackend(
            duid,
            targetDuid,
            replicatedTask.duid,
            kindDuid,
            isForward !== undefined ? !isForward : undefined
          ),
          targetDuid,
        ])
        .filter((e): e is [Relationship, string] => !!e[0])
        .map(([relationship, sourceDuid]) => ({ ...relationship, sourceDuid }))
    );

    if (!propertiesAndParentOnly) {
      // Add the task - doc relationships
      res.taskDocRelationshipCreates.push(
        ...(await this.getDocsRelatedToTaskOrdered(task)).map((doc) => {
          const taskDocRelationshipCreate = { duid: makeDuid(), taskDuid: replicatedTask.duid, docDuid: doc.duid };
          this.$createTaskDocRelationship(taskDocRelationshipCreate);
          return taskDocRelationshipCreate;
        })
      );

      // Add the links
      res.linkCreates.push(
        ...task.links
          .filter((e) => e.kind === TaskLinkKind.STANDARD)
          .map((link) => {
            const taskLinkCreate = { ...link, duid: makeDuid(), taskDuid: replicatedTask.duid };
            this.$addLinksNoBackend(replicatedTask, [taskLinkCreate]);
            return taskLinkCreate;
          })
      );

      // Add the attachments
      const oldAttachments = await this.getAttachmentsByDuidsOrdered(task.attachmentDuids);
      const files = await createFilesFromAttachments(oldAttachments);
      const attachmentConfigs = oldAttachments.map((attachment, i) => ({
        file: files[i],
        order: attachment.order,
        partialAttachment: { colorHex: oldAttachments[i].colorHex },
      }));
      const attachmentsAndCreates = await this.createAttachments(attachmentConfigs, {
        taskDuid: replicatedTask.duid,
        noBackend: true,
      });
      replicatedTask.attachmentDuids.push(...attachmentsAndCreates.map((e) => e.attachment.duid));
      res.attachmentCreates.push(...attachmentsAndCreates.map((e) => e.create));

      // Replicate the non parent and non duplicate relationships
      const newRelationships = this.getRelationships(task)
        .filter((e) => e.kindDuid !== parentKindDuid && e.kindDuid !== duplicateKindDuid)
        .map((relationship) => {
          // TODO unclear why reactive is needed here. should figure it out and remove
          const newRelationship = reactive({ ...relationship, duid: makeDuid() });
          replicatedTask.relationships.push(newRelationship);
          return [
            this.$createRelationshipOneDirectionNoBackend(
              newRelationship.duid,
              newRelationship.targetDuid,
              replicatedTask.duid,
              newRelationship.kindDuid,
              newRelationship.isForward !== undefined ? !newRelationship.isForward : undefined
            ),
            relationship.targetDuid,
          ];
        })
        .filter((e): e is [Relationship, string] => !!e[0])
        .map(([relationship, sourceDuid]) => ({ ...relationship, sourceDuid }));
      res.relationshipCreates.push(...newRelationships);

      // Add child tasks recursively
      const subtaskRelationships = this.getRelationshipsByKindKind(task, RelationshipKindKind.PARENT_OF, true);
      const subtaskOrders = getOrdersBetween(replicatedTask.order, undefined, subtaskRelationships.length);

      await Promise.all(
        subtaskRelationships.map(async (relationship, i) => {
          const subtask = await this.getTaskByDuid(relationship.targetDuid);
          if (!subtask) {
            return;
          }

          const subtaskRes = await this.$replicateTaskRecursive(
            subtask,
            subtaskOrders[i],
            { ...partialTask, relationships: [] },
            {
              ...options,
              resetSize,
              maintainTitle: true,
              maintainStatus,
            }
          );
          res.taskCreates.push(...subtaskRes.taskCreates);
          res.relationshipCreates.push(...subtaskRes.relationshipCreates);
          res.taskDocRelationshipCreates.push(...subtaskRes.taskDocRelationshipCreates);
          res.linkCreates.push(...subtaskRes.linkCreates);
          res.attachmentCreates.push(...subtaskRes.attachmentCreates);

          const replicatedSubtask = subtaskRes.replicatedTask;
          const newRelationship = await this.createRelationship(
            replicatedTask.duid,
            replicatedSubtask.duid,
            parentKindDuid,
            { noBackend: true }
          );
          if (newRelationship) {
            res.relationshipCreates.push({ ...newRelationship, sourceDuid: replicatedTask.duid });
          }
        })
      );
    }

    if (!propertiesAndParentOnly || includeDuplicate) {
      // TODO unclear why reactive is needed here. should figure it out and remove
      const newRelationshipRaw = reactive({
        duid: makeDuid(),
        kindDuid: duplicateKindDuid ?? "",
        targetDuid: task.duid,
      });
      const newRelationship = this.$createRelationshipOneDirectionNoBackend(
        newRelationshipRaw.duid,
        newRelationshipRaw.targetDuid,
        replicatedTask.duid,
        newRelationshipRaw.kindDuid,
        undefined
      );
      if (newRelationship) {
        replicatedTask.relationships.push(newRelationshipRaw);
        res.relationshipCreates.push({ ...newRelationship, sourceDuid: task.duid });
      }
    }

    return { ...res, replicatedTask };
  },
  $createRelationshipOneDirectionNoBackend(
    relationshipDuid,
    modTaskDuid,
    otherTaskDuid,
    relationshipKindDuid,
    isForward
  ) {
    const task = this.getTaskByDuidIfLoaded(modTaskDuid);
    if (!task) {
      return undefined;
    }
    const userStore = this.$useUserStore();

    // TODO unclear why reactive is needed here. should figure it out and remove
    const relationship = reactive({
      duid: relationshipDuid,
      kindDuid: relationshipKindDuid,
      targetDuid: otherTaskDuid,
      isForward,
    });
    task.relationships.push(relationship);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = userStore.duid;

    const parentKindDuid = this.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;
    if (relationshipKindDuid === parentKindDuid && isForward === false) {
      this.$addOrRemoveRollupUiOnly(task, { remove: false });
    }

    return relationship;
  },
  $updateTaskSubscribersNoBackend(task, potentialSubscriberDuids) {
    const currentSubscriberDuids = new Set(task.subscriberDuids);
    const newSubscriberDuids = potentialSubscriberDuids.filter((e) => !currentSubscriberDuids.has(e));

    // eslint-disable-next-line no-param-reassign
    task.subscriberDuids = [...currentSubscriberDuids, ...newSubscriberDuids];

    return newSubscriberDuids;
  },
  $addLinksNoBackend(task, linkCreates) {
    // TODO unclear why reactive is needed here. should figure it out and remove
    task.links.push(
      ...linkCreates.map((e) =>
        reactive({
          kind: TaskLinkKind.STANDARD,
          title: null,
          iconUrl: null,
          adtl: {},
          ...e,
        })
      )
    );
  },
  $updateLinksNoBackend(task, linkUpdates) {
    linkUpdates.forEach((linkUpdate) => {
      const link = task.links.find((e) => e.duid === linkUpdate.duid);
      if (!link) {
        return;
      }

      Object.assign(link, linkUpdate);
    });
  },
  $deleteLinksNoBackend(task, linkDuids) {
    filterInPlace(task.links, (e: TaskLink) => !linkDuids.includes(e.duid));
  },
  $updateDartboardTopTaskOrderNoBackend(dartboardDuid, changingTask, oldTaskOrder) {
    const dartboard = this.getDartboardByDuid(dartboardDuid);
    if (!dartboard) {
      return;
    }

    if (!changingTask.inTrash && !changingTask.drafterDuid && changingTask.dartboardDuid === dartboard.duid) {
      if (!dartboard.topTaskOrder) {
        dartboard.topTaskOrder = changingTask.order;
        return;
      }

      if (stringComparator(changingTask.order, dartboard.topTaskOrder) < 0) {
        dartboard.topTaskOrder = changingTask.order;
        return;
      }
    }

    const allTasks = this.getAllLoadedTasksForUiOnly();
    const tasks = allTasks
      .filter((task) => task.dartboardDuid === dartboard.duid && !task.inTrash && !task.drafterDuid)
      .sort(makeStringComparator((e) => e.order));

    if (tasks.length === 0) {
      return;
    }

    const firstTaskOrder = tasks[0].order;
    if (
      !dartboard.topTaskOrder ||
      stringComparator(firstTaskOrder, dartboard.topTaskOrder) < 0 ||
      dartboard.topTaskOrder === oldTaskOrder
    ) {
      dartboard.topTaskOrder = firstTaskOrder;
    }
  },
  $createOrUpdateTask(task, extras = {}) {
    const { hardUpdate = false, skipSummaryStatisticsAndRollup = false } = extras;

    /* Shared */
    const appStore = this.$useAppStore();
    const pageStore = this.$usePageStore();

    const currentTask = this.getTaskByDuidIfLoaded(task.duid);
    const oldTask = currentTask ? { ...currentTask } : undefined;
    const oldDartboardDuid = currentTask?.dartboardDuid;
    const oldTaskOrder = currentTask?.order;

    if (this.$isEntityDuidPendingDeletion(task.duid)) {
      return;
    }

    if (
      !appStore.tcmOpen &&
      currentTask &&
      currentTask.createdByDuid &&
      this.getUserByDuid(currentTask.createdByDuid)?.kind === USER_KIND_DART_AI &&
      currentTask.recommendationDuid
    ) {
      const visualization = appStore.getActiveVisualization();
      const taskElement = visualization?.getHtml(currentTask.duid);
      if (!taskElement) {
        return;
      }
      appStore.showFeedbackTooltip(taskElement, [currentTask.recommendationDuid]);
    }

    // TODO remove this check when we are filtering out spurious tasks on BE of WS
    if (
      (!pageStore.isPublicView && !this.getDartboardByDuid(task.dartboardDuid)) ||
      (task.drafterDuid && task.drafterDuid !== this.$useUserStore().duid)
    ) {
      if (currentTask) {
        this.$deleteTask(currentTask);
      }
      return;
    }

    // Needs to happen before updates to pagination states
    if (!skipSummaryStatisticsAndRollup) {
      if (!currentTask) {
        this.$updateSummaryStatisticsForTaskChange(undefined, task);
      } else {
        this.$updateSummaryStatisticsForTaskChange(currentTask, { ...currentTask, ...task });
      }
    }

    this.$unblacklistEntityDuid(task.duid);
    this.$maybeRemoveInvalidEntityFromPaginationStates(task);

    // Also re-validate all ancestors since they might be absentee parents that no longer qualify
    const ancestorDuids = this.getAncestorDuidsUiOnly(task);
    const ancestors = this.getTasksByDuidsIfLoaded(ancestorDuids, { includeTrashed: false, includeDraft: false });
    ancestors.forEach((ancestor) => {
      this.$maybeRemoveInvalidEntityFromPaginationStates(ancestor);
    });

    if (!task.drafterDuid) {
      this.$addEntitiesToPaginationStates(TASK_PAGE_PAGINATION_KEY, [task]);
    }

    // Add attachments to pagination state if attachment pagination exists for this task
    const attachmentPaginationKey = `attachments:${task.duid}`;
    const attachmentPaginationState = this.$getPaginationState(attachmentPaginationKey, null);
    if (attachmentPaginationState && task.attachmentDuids.length > 0) {
      const attachments = task.attachmentDuids
        .map((attachmentDuid) => this.getAttachmentByDuidIfLoaded(attachmentDuid))
        .filter(isDefined);
      if (attachments.length > 0) {
        this.$addEntitiesToPaginationStates(attachmentPaginationKey, attachments);
      }
    }

    /* Create */
    if (!currentTask) {
      this._duidsToTasks.set(task.duid, task);
      if (this.$isPrimaryDraft(task)) {
        this.taskDraftDuid = task.duid;
      }

      this.$updateDartboardTopTaskOrderNoBackend(task.dartboardDuid, task, undefined);
      if (!skipSummaryStatisticsAndRollup) {
        this.$addOrRemoveRollupUiOnly(task, { remove: false });
      }
      return;
    }

    /* Update */
    if (currentTask.updatedAt > task.updatedAt) {
      printOrCaptureMessage("Stale task update clobbered a newer local update", {
        taskDuid: task.duid,
        localUpdatedAt: currentTask.updatedAt,
        incomingUpdatedAt: task.updatedAt,
        localUpdatedByDuid: currentTask.updatedByDuid,
        incomingUpdatedByDuid: task.updatedByDuid,
        dartboardDuid: task.dartboardDuid,
        hardUpdate,
      });
    }
    Object.assign(currentTask, task);
    if (!skipSummaryStatisticsAndRollup) {
      this.$updateRollupForTaskUpdatesUiOnly({ ...currentTask, ...task }, oldTask);
    }

    if (this.$isPrimaryDraft(currentTask)) {
      this.taskDraftDuid = currentTask.duid;
    } else if (this.taskDraft && this.taskDraft.duid === currentTask.duid) {
      this.taskDraftDuid = null;
    }

    if (oldDartboardDuid && oldDartboardDuid !== task.dartboardDuid) {
      this.$updateDartboardTopTaskOrderNoBackend(oldDartboardDuid, task, oldTaskOrder);
      this.$updateDartboardTopTaskOrderNoBackend(task.dartboardDuid, task, undefined);
    } else {
      this.$updateDartboardTopTaskOrderNoBackend(task.dartboardDuid, task, oldTaskOrder);
    }

    if (hardUpdate) {
      this.$useAppStore().taskDetail?.hardUpdateDescription();
    }
  },
  $deleteTask(task) {
    this._duidsToTasks.delete(task.duid);
    this.$updateSummaryStatisticsForTaskChange(task, undefined);
    this.$removeEntitiesFromPaginationStates([task]);
    this.$addOrRemoveRollupUiOnly(task, { remove: true });

    if (this.taskDraft && this.taskDraft.duid === task.duid) {
      this.taskDraftDuid = null;
    }

    this.$useAppStore().selectedTaskDuids.delete(task.duid);
  },
  $isPrimaryDraft(task) {
    if (
      !task.drafterDuid ||
      (task.createdByDuid && this.getUserByDuid(task.createdByDuid)?.kind === USER_KIND_DART_AI)
    ) {
      return false;
    }
    const parentDuids = this.getRelationshipsByKindKind(task, RelationshipKindKind.PARENT_OF, false);
    if (parentDuids.length === 0) {
      return true;
    }
    const parent = this.getTaskByDuidIfLoaded(parentDuids[0].targetDuid);
    if (!parent) {
      return true;
    }
    return !parent.drafterDuid;
  },
  $setDraftTask(task) {
    this._duidsToTasks.set(task.duid, task);
    if (this.$isPrimaryDraft(task)) {
      this.taskDraftDuid = task.duid;
    }
  },
  $remapPropertiesOfTasksAsNeeded(updates, onRemapFailed) {
    if (updates.length === 0) {
      return Promise.resolve([]);
    }

    const appStore = this.$useAppStore();

    // Find all updates that need remapping
    const remapModalItems: Map<string, RemapModalConfigItem> = new Map();
    updates.forEach((update) => {
      const suggestedUpdate = checkTaskConstraints(update, this);

      // Remap default status for task kind updates
      if (suggestedUpdate.statusDuid) {
        const task = this.getTaskByDuidIfLoaded(update.duid);
        if (!task) {
          return;
        }

        const groupByDefinition = appStore.groupByDefinitionList.find(
          (e) => e.property.duid === this.defaultStatusProperty.duid
        );
        if (!groupByDefinition) {
          return;
        }

        const groups = groupByDefinition.groups.map((e) => ({ ...e, icon: markRaw(e.icon) }));
        const oldStatusDuid = update.statusDuid ?? task.statusDuid;
        const oldOption = groups.find((e) => e.value === oldStatusDuid);
        if (!oldOption) {
          return;
        }

        const taskKind = this.getTaskKindByDuid(update.kindDuid ?? task.kindDuid);
        const hiddenStatusDuids = new Set(taskKind?.hiddenStatusDuids ?? []);

        remapModalItems.set(`${this.defaultStatusProperty.duid}-${oldStatusDuid}`, {
          propertyDuid: this.defaultStatusProperty.duid,
          oldOption,
          options: groups.filter((e) => typeof e.value === "string" && !hiddenStatusDuids.has(e.value)),
          proposedValue: suggestedUpdate.statusDuid,
        });
      }
    });

    // No remapping needed
    if (remapModalItems.size === 0) {
      return Promise.resolve(updates);
    }

    // Show the remap modal
    const remapPromise = new Promise<TaskUpdate[]>((resolve) => {
      appStore.remapModal = {
        items: Array.from(remapModalItems.values()),
        remap: (items) => {
          resolve(
            [...updates].map((update) => {
              const res = { ...update };
              const task = this.getTaskByDuidIfLoaded(update.duid);
              if (!task) {
                return res;
              }

              let remappedUpdate;
              // Remap default status for task kind updates
              const taskKindDuid = update.kindDuid ?? this.getTaskByDuidIfLoaded(update.duid)?.kindDuid;
              if (taskKindDuid) {
                const oldStatusDuid = update.statusDuid ?? task.statusDuid;
                remappedUpdate = items.find(
                  (e) => e.propertyDuid === this.defaultStatusProperty.duid && e.oldValue === oldStatusDuid
                );
                if (remappedUpdate) {
                  res.statusDuid = remappedUpdate.newValue as string;
                }
              }

              return res;
            })
          );
        },
        cancel: () => {
          resolve([]);
          onRemapFailed();
        },
      };
    });

    return remapPromise;
  },
  $updateSummaryStatisticsForTaskChange(oldTask: Task | undefined, newTask: Task | undefined) {
    const appStore = this.$useAppStore();
    const isCompleted = (task: Task) => {
      const status = this.getStatusByDuid(task.statusDuid);
      return status && COMPLETED_STATUS_KINDS.has(status.kind);
    };

    const updateStatsForTask = (key: string, task: Task, isRemoving: boolean) => {
      const currentStats = this._keyToSummaryStatistics.get(key);
      if (!currentStats) {
        return;
      }

      const multiplier = isRemoving ? -1 : 1;
      const taskSize = task.size ?? 0;
      const taskCompleted = isCompleted(task);

      const newStats = {
        ...currentStats,
        count: Math.max(0, currentStats.count + multiplier),
        points: Math.max(0, currentStats.points + multiplier * taskSize),
        completedCount: Math.max(0, currentStats.completedCount + (taskCompleted ? multiplier : 0)),
        completedPoints: Math.max(0, currentStats.completedPoints + (taskCompleted ? multiplier * taskSize : 0)),
      };

      this._keyToSummaryStatistics.set(key, newStats);
    };

    const getPropertyValues = (task: Task, propertyDuid: string): string[] => {
      const value = getPropertyValueFromTaskByDuid(propertyDuid, task);
      if (value === undefined) {
        return [];
      }
      const arr = Array.isArray(value) ? value : [value];
      if (arr.length === 0) {
        if (
          propertyDuid === this.defaultAssigneesProperty.duid ||
          propertyDuid === this.defaultReviewersProperty.duid
        ) {
          return [UNASSIGNED_PSEUDO_ASSIGNEE_KEY];
        }
        return [NULL_PSEUDO_VALUE];
      }
      return arr.map((v) => {
        if (v === null) {
          if (propertyDuid === this.defaultPriorityProperty.duid) {
            return UNPRIORITIZED_PSEUDO_VALUE;
          }
          if (propertyDuid === this.defaultSizeProperty.duid) {
            return UNSIZED_SIZE_GROUP_BY_KEY;
          }
        }
        return getPropertyValueStr(v as PropertyValue);
      });
    };

    const getTaskGroupKeys = (task: Task, pageDuid: string): string[] => {
      const keys: string[] = [];

      if (appStore.groupBy && appStore.groupBy !== UNGROUPED_PSEUDO_GROUP_BY) {
        const columnValues = getPropertyValues(task, appStore.groupBy);
        keys.push(...columnValues.map((v) => getPageKey(pageDuid, v)));

        if (appStore.subgroupBy && appStore.subgroupBy !== UNGROUPED_PSEUDO_GROUP_BY) {
          const swimlaneValues = getPropertyValues(task, appStore.subgroupBy);
          keys.push(...swimlaneValues.map((v) => getPageKey(pageDuid, v)));
          columnValues.forEach((colVal) => {
            swimlaneValues.forEach((swimVal) => {
              keys.push(getPageKey(pageDuid, `${colVal}-${swimVal}`));
            });
          });
        }
      }

      if (!appStore.groupBy && appStore.subgroupBy && appStore.subgroupBy !== UNGROUPED_PSEUDO_GROUP_BY) {
        const swimlaneValues = getPropertyValues(task, appStore.subgroupBy);
        keys.push(...swimlaneValues.map((v) => getPageKey(pageDuid, v)));
      }

      return keys;
    };

    Array.from(this._keyToSummaryStatistics.keys()).forEach((key) => {
      const paginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, key);
      const pageDuid = getPageDuidFromKey(key);
      const parsedKey = JSON.parse(key);
      const groupId = parsedKey[1];

      if (groupId === null) {
        if (!paginationState || !paginationState.validator) {
          return;
        }
        if (oldTask && paginationState.duids.has(oldTask.duid)) {
          updateStatsForTask(key, oldTask, true);
        }
        if (newTask && paginationState.validator(newTask)) {
          updateStatsForTask(key, newTask, false);
        }
        return;
      }

      if (
        groupId === UNASSIGNED_PSEUDO_ASSIGNEE_KEY ||
        groupId === UNSIZED_SIZE_GROUP_BY_KEY ||
        groupId === UNPRIORITIZED_PSEUDO_VALUE ||
        groupId === NULL_PSEUDO_VALUE
      ) {
        const mainPaginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, getPageKey(pageDuid, null));

        if (oldTask && (paginationState?.duids.has(oldTask.duid) || mainPaginationState?.duids.has(oldTask.duid))) {
          const oldGroupKeys = getTaskGroupKeys(oldTask, pageDuid);
          if (oldGroupKeys.includes(key)) {
            updateStatsForTask(key, oldTask, true);
          }
        }

        if (
          newTask &&
          ((paginationState?.validator && paginationState.validator(newTask)) ||
            (mainPaginationState?.validator && mainPaginationState.validator(newTask)))
        ) {
          const newGroupKeys = getTaskGroupKeys(newTask, pageDuid);
          if (newGroupKeys.includes(key)) {
            updateStatsForTask(key, newTask, false);
          }
        }

        return;
      }

      if (appStore.groupBy && appStore.groupBy !== UNGROUPED_PSEUDO_GROUP_BY) {
        const mainPaginationState = this.$getPaginationState(TASK_PAGE_PAGINATION_KEY, getPageKey(pageDuid, null));

        if (
          oldTask &&
          ((paginationState && paginationState.duids.has(oldTask.duid)) ||
            (mainPaginationState && mainPaginationState.duids.has(oldTask.duid)))
        ) {
          const oldGroupKeys = getTaskGroupKeys(oldTask, pageDuid);
          if (oldGroupKeys.includes(key)) {
            updateStatsForTask(key, oldTask, true);
          }
        }
        if (
          newTask &&
          ((paginationState?.validator && paginationState.validator(newTask)) ||
            (mainPaginationState?.validator && mainPaginationState.validator(newTask)))
        ) {
          const newGroupKeys = getTaskGroupKeys(newTask, pageDuid);
          if (newGroupKeys.includes(key)) {
            updateStatsForTask(key, newTask, false);
          }
        }
      }
    });
  },
  $getDateRollupFromChildren(ancestor, propertyDuid, propertyKind, excludingChildDuid = null) {
    const getDateRange = (value: unknown): [string | null, string | null] => {
      if (!Array.isArray(value)) {
        return [null, null];
      }
      const start = value.length > 0 && typeof value[0] === "string" ? value[0] : null;
      const due = value.length > 1 && typeof value[1] === "string" ? value[1] : null;
      return [start, due];
    };

    const childDuids = this.getRelationshipsByKindKind(ancestor, RelationshipKindKind.PARENT_OF, true).map(
      (e) => e.targetDuid
    );
    const startDates: string[] = [];
    const dueDates: string[] = [];

    childDuids.forEach((childDuid) => {
      if (excludingChildDuid && childDuid === excludingChildDuid) {
        return;
      }
      const child = this.getTaskByDuidIfLoaded(childDuid);
      if (!child || child.inTrash || child.drafterDuid) {
        return;
      }

      let effectiveStart: string | null;
      let effectiveDue: string | null;

      if (propertyKind === PropertyKind.DEFAULT_DATES) {
        const { rolledUpStartAt, rolledUpDueAt } = this.getTaskDates(child, true);
        effectiveStart = rolledUpStartAt;
        effectiveDue = rolledUpDueAt;
      } else {
        const [childStart, childDue] = getDateRange(child.properties[propertyDuid]);
        const [rollupStart, rollupDue] = getDateRange(child.rollup[propertyDuid]);
        effectiveStart = childStart ?? rollupStart;
        effectiveDue = childDue ?? rollupDue;
      }

      if (effectiveStart) {
        startDates.push(effectiveStart);
      }
      if (effectiveDue) {
        dueDates.push(effectiveDue);
      }
    });

    const minStart = startDates.length > 0 ? startDates.sort()[0] : null;
    const maxDue = dueDates.length > 0 ? dueDates.sort().reverse()[0] : null;
    return [minStart, maxDue];
  },
  $addOrRemoveRollupUiOnly(task, options) {
    const { remove } = options;

    if (task.drafterDuid || task.inTrash) {
      return;
    }

    const propertyDiffs: PropertyDiff[] = [];
    const multiplier = remove ? -1 : 1;

    const taskSize = task.size || 0;
    const sizeRollupValue = task.rollup[this.defaultSizeProperty.duid];
    const sizeRollup = typeof sizeRollupValue === "number" ? sizeRollupValue : 0;
    const sizeContribution = (taskSize + sizeRollup) * multiplier;

    propertyDiffs.push({
      propertyDuid: this.defaultSizeProperty.duid,
      propertyKind: PropertyKind.DEFAULT_SIZE,
      diff: sizeContribution,
    });

    const numberProperties = this.getPropertyByKind(PropertyKind.NUMBER);
    const taskPropertyDuids = Object.keys(task.properties);

    numberProperties.forEach((property) => {
      if (!taskPropertyDuids.includes(property.duid)) {
        return;
      }
      const propertyValue = task.properties[property.duid];
      const numPropertyValue = typeof propertyValue === "number" ? propertyValue : 0;
      const rollupValue = task.rollup[property.duid];
      const numRollupValue = typeof rollupValue === "number" ? rollupValue : 0;
      const totalContribution = (numPropertyValue + numRollupValue) * multiplier;

      propertyDiffs.push({ propertyDuid: property.duid, propertyKind: PropertyKind.NUMBER, diff: totalContribution });
    });

    propertyDiffs.push({
      propertyDuid: this.defaultDatesProperty.duid,
      propertyKind: PropertyKind.DEFAULT_DATES,
      diff: { oldStart: null, newStart: null, oldDue: null, newDue: null, remove },
    });

    const dateProperties = this.getPropertyByKind(PropertyKind.DATES);
    dateProperties.forEach((property) => {
      if (!taskPropertyDuids.includes(property.duid)) {
        return;
      }

      propertyDiffs.push({
        propertyDuid: property.duid,
        propertyKind: PropertyKind.DATES,
        diff: { oldStart: null, newStart: null, oldDue: null, newDue: null, remove },
      });
    });

    if (propertyDiffs.length === 0) {
      return;
    }

    this.$updateRollupUiOnly(task, propertyDiffs);
  },
  $updateRollupForTaskUpdatesUiOnly(task, oldTask) {
    const propertyDiffs: PropertyDiff[] = [];

    const isOldTaskValid = oldTask && !oldTask.drafterDuid && !oldTask.inTrash;
    const isNewTaskValid = !task.drafterDuid && !task.inTrash;

    const oldTaskSize = isOldTaskValid ? oldTask.size || 0 : 0;
    const oldSizeRollupValue = isOldTaskValid ? oldTask.rollup[this.defaultSizeProperty.duid] : undefined;
    const oldSizeRollup = typeof oldSizeRollupValue === "number" ? oldSizeRollupValue : 0;
    const oldSize = oldTaskSize + oldSizeRollup;

    const newTaskSize = isNewTaskValid ? task.size || 0 : 0;
    const newSizeRollupValue = isNewTaskValid ? task.rollup[this.defaultSizeProperty.duid] : undefined;
    const newSizeRollup = typeof newSizeRollupValue === "number" ? newSizeRollupValue : 0;
    const newSize = newTaskSize + newSizeRollup;

    const sizeDiff = newSize - oldSize;

    if (sizeDiff !== 0) {
      propertyDiffs.push({
        propertyDuid: this.defaultSizeProperty.duid,
        propertyKind: PropertyKind.DEFAULT_SIZE,
        diff: sizeDiff,
      });
    }

    const numberProperties = this.getPropertyByKind(PropertyKind.NUMBER);

    numberProperties.forEach((property) => {
      const oldPropertyValue = isOldTaskValid ? oldTask?.properties[property.duid] : undefined;
      const oldNumPropertyValue = typeof oldPropertyValue === "number" ? oldPropertyValue : 0;
      const oldRollupValue = isOldTaskValid ? oldTask.rollup[property.duid] : undefined;
      const oldNumRollupValue = typeof oldRollupValue === "number" ? oldRollupValue : 0;
      const oldValue = oldNumPropertyValue + oldNumRollupValue;

      const newPropertyValue = isNewTaskValid ? task.properties[property.duid] : undefined;
      const newNumPropertyValue = typeof newPropertyValue === "number" ? newPropertyValue : 0;
      const newRollupValue = isNewTaskValid ? task.rollup[property.duid] : undefined;
      const newNumRollupValue = typeof newRollupValue === "number" ? newRollupValue : 0;
      const newValue = newNumPropertyValue + newNumRollupValue;

      const diff = newValue - oldValue;

      if (diff === 0) {
        return;
      }

      propertyDiffs.push({ propertyDuid: property.duid, propertyKind: PropertyKind.NUMBER, diff });
    });

    const oldStart = isOldTaskValid ? oldTask?.startAt : null;
    const newStart = isNewTaskValid ? task.startAt : null;
    const oldDue = isOldTaskValid ? oldTask?.dueAt : null;
    const newDue = isNewTaskValid ? task.dueAt : null;

    if (oldStart !== newStart || oldDue !== newDue) {
      propertyDiffs.push({
        propertyDuid: this.defaultDatesProperty.duid,
        propertyKind: PropertyKind.DEFAULT_DATES,
        diff: { oldStart, newStart, oldDue, newDue, remove: false },
      });
    }

    const dateProperties = this.getPropertyByKind(PropertyKind.DATES);
    dateProperties.forEach((property) => {
      const oldPropertyValue = isOldTaskValid ? oldTask?.properties[property.duid] : undefined;
      const oldDates = Array.isArray(oldPropertyValue) ? oldPropertyValue : [null, null];
      const oldPropStart = oldDates.length > 0 && typeof oldDates[0] === "string" ? oldDates[0] : null;
      const oldPropDue = oldDates.length > 1 && typeof oldDates[1] === "string" ? oldDates[1] : null;

      const newPropertyValue = isNewTaskValid ? task.properties[property.duid] : undefined;
      const newDates = Array.isArray(newPropertyValue) ? newPropertyValue : [null, null];
      const newPropStart = newDates.length > 0 && typeof newDates[0] === "string" ? newDates[0] : null;
      const newPropDue = newDates.length > 1 && typeof newDates[1] === "string" ? newDates[1] : null;

      if (oldPropStart !== newPropStart || oldPropDue !== newPropDue) {
        propertyDiffs.push({
          propertyDuid: property.duid,
          propertyKind: PropertyKind.DATES,
          diff: {
            oldStart: oldPropStart,
            newStart: newPropStart,
            oldDue: oldPropDue,
            newDue: newPropDue,
            remove: false,
          },
        });
      }
    });

    if (propertyDiffs.length === 0) {
      return;
    }

    this.$updateRollupUiOnly(task, propertyDiffs);
  },
  $updateRollupUiOnly(task, propertyDiffs) {
    if (propertyDiffs.length === 0) {
      return;
    }

    const ancestorDuids = this.getAncestorDuidsUiOnly(task);
    ancestorDuids.reverse().forEach((ancestorDuid) => {
      const ancestor = this.getTaskByDuidIfLoaded(ancestorDuid);
      if (!ancestor || ancestor.inTrash) {
        return;
      }

      propertyDiffs.forEach(({ propertyDuid, propertyKind, diff }) => {
        if (typeof diff === "number") {
          if (diff === 0) {
            return;
          }
          const currentRollupValue = ancestor.rollup[propertyDuid];
          const currentRollup = typeof currentRollupValue === "number" ? currentRollupValue : 0;
          const newRollupValue = Math.max(0, currentRollup + diff);
          ancestor.rollup[propertyDuid] = newRollupValue;
        } else if (propertyKind === PropertyKind.DEFAULT_DATES || propertyKind === PropertyKind.DATES) {
          const excludingChildDuid = diff.remove ? task.duid : null;
          const recalculatedDates = this.$getDateRollupFromChildren(
            ancestor,
            propertyDuid,
            propertyKind,
            excludingChildDuid
          );
          ancestor.rollup[propertyDuid] = recalculatedDates;
        }
      });
    });
  },
  $cleanupTasksPropertyValuesNoBackend(entityDuids, propertyDuids, { removeAllValues = false } = {}) {
    const entityDuidsSet = new Set(entityDuids);

    const taskUpdates = this.getAllLoadedTasksForUiOnly({ includeTrashed: true, includeDraft: true }).map((task) => {
      const properties = { ...task.properties };
      const rollup = { ...task.rollup };

      propertyDuids.forEach((propertyDuid) => {
        if (propertyDuid in properties) {
          if (removeAllValues) {
            delete properties[propertyDuid];
            return;
          }

          const propertyValue = properties[propertyDuid];

          if (Array.isArray(propertyValue)) {
            const filteredValues = propertyValue.filter((v) => isString(v) && !entityDuidsSet.has(v));
            if (filteredValues.length === 0) {
              delete properties[propertyDuid];
            } else {
              properties[propertyDuid] = filteredValues.filter(isString);
            }
          } else if (isString(propertyValue) && entityDuidsSet.has(propertyValue)) {
            delete properties[propertyDuid];
          }
        }

        if (removeAllValues && propertyDuid in rollup) {
          delete rollup[propertyDuid];
        }
      });

      return { duid: task.duid, properties, rollup };
    });

    if (taskUpdates.length === 0) {
      return;
    }

    this.updateTasks(taskUpdates, { noUndo: true, noCelebrate: true, noBackend: true });
  },
};

export { actions, getters };
